import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TablePagination,
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { SearchResult, SearchFormData } from '../types';
import { validatePhoneNumbers, validateEmailAddress, generateAIInsights, getTransactions, getPhilanthropy } from '../services/api';
import { buildAIProfileData } from '../services/aiProfileData';
import ScoreGauge from './ScoreGauge';
import AIInsightOutput, { AIInsightMarkdown } from './AIInsightOutput';

interface TabbedResultsProps {
  data: SearchResult;
  searchCriteria?: SearchFormData;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`results-tabpanel-${index}`}
      aria-labelledby={`results-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// ─── Feature flag: set to true to re-enable KnowledgeCore AI Analysis ───────
const AI_ANALYSIS_ENABLED = false;
// ─────────────────────────────────────────────────────────────────────────────

const TabbedResults: React.FC<TabbedResultsProps> = ({ data, searchCriteria }) => {
  const [value, setValue] = useState(0);
  const [selectedSubSection, setSelectedSubSection] = useState<{ [key: string]: string }>({});
  const [aiInsightsLoading, setAiInsightsLoading] = useState<{ [key: string]: boolean }>({});
  const [aiInsightsResults, setAiInsightsResults] = useState<{ [key: string]: string }>({});
  const [biologicalSummaryLoading, setBiologicalSummaryLoading] = useState(false);
  const [biologicalSummary, setBiologicalSummary] = useState<string | null>(null);
  const [consumerBehaviorSummaryLoading, setConsumerBehaviorSummaryLoading] = useState(false);
  const [consumerBehaviorSummary, setConsumerBehaviorSummary] = useState<string | null>(null);
  const [financialSummaryLoading, setFinancialSummaryLoading] = useState(false);
  const [financialSummary, setFinancialSummary] = useState<string | null>(null);
  const [fieldTableCollapsed, setFieldTableCollapsed] = useState<{ [key: string]: boolean }>({});
  const [phoneValidationLoading, setPhoneValidationLoading] = useState(false);
  const [phoneValidationData, setPhoneValidationData] = useState<any>(null);
  const [emailValidationLoading, setEmailValidationLoading] = useState(false);
  const [emailValidationData, setEmailValidationData] = useState<any>(null);
  const [transactionData, setTransactionData] = useState<any>(null);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [datairisData, setDatairisData] = useState<any>(null);
  const [philanthropyData, setPhilanthropyData] = useState<any>(null);
  const [philanthropyLoading, setPhilanthropyLoading] = useState(false);
  const [fecContributionsData, setFecContributionsData] = useState<any>(null);
  const [irsForm990Data, setIrsForm990Data] = useState<any>(null);
  const [googleNewsData, setGoogleNewsData] = useState<any>(null);

  // Read Apollo data directly from prop — no state needed, avoids extra render cycle
  const apolloBusinessData = data?.apollo_business_data ?? null;
  const [googleNewsPaginationPage, setGoogleNewsPaginationPage] = useState(0);
  const [googleNewsPaginationRowsPerPage, setGoogleNewsPaginationRowsPerPage] = useState(10);
  const [fecPaginationPage, setFecPaginationPage] = useState(0);
  const [fecPaginationRowsPerPage, setFecPaginationRowsPerPage] = useState(10);
  const [irsPaginationPage, setIrsPaginationPage] = useState(0);
  const [irsPaginationRowsPerPage, setIrsPaginationRowsPerPage] = useState(10);
  const [apolloPaginationPage, setApolloPaginationPage] = useState(0);
  const [apolloPaginationRowsPerPage, setApolloPaginationRowsPerPage] = useState(10);
  const [apolloSubTab, setApolloSubTab] = useState<'personal' | 'organization'>('personal');
  const [expandedEmploymentIndices, setExpandedEmploymentIndices] = useState<Set<number>>(new Set());

  // Auto-load FEC contributions data if available in results
  React.useEffect(() => {
    if (data && data.fec_contributions && !fecContributionsData) {
      console.log('Setting FEC contributions data from results...');
      console.log('Full FEC contributions object:', data.fec_contributions);
      setFecContributionsData(data.fec_contributions);
    }
  }, [data, fecContributionsData]);

  // Auto-load IRS Form 990 data if available in results
  React.useEffect(() => {
    if (data && data.irs_form990_records && !irsForm990Data) {
      console.log('Setting IRS Form 990 data from results...');
      console.log('Full IRS Form 990 object:', data.irs_form990_records);
      setIrsForm990Data(data.irs_form990_records);
    }
  }, [data, irsForm990Data]);

  // Auto-load Google News data if available in results
  React.useEffect(() => {
    if (data && data.google_news) {
      console.log('Setting Google News data from results...');
      setGoogleNewsData(data.google_news);
    } else {
      setGoogleNewsData(null);
    }
  }, [data]);

  // Auto-load DataIris data if available in results
  React.useEffect(() => {
    console.log('useEffect triggered - data changed');
    console.log('Current data:', data);
    console.log('Current datairisData state:', datairisData);
    
    if (data && data.datairis && !datairisData) {
      console.log('Setting DataIris data from results...');
      console.log('Full DataIris object:', data.datairis);
      setDatairisData(data.datairis);
    }
  }, [data, datairisData]);

  // Auto-fetch transactions when data changes and constituent_id is available
  React.useEffect(() => {
    const fetchTransactionsAuto = async () => {
      // Get constituent_id from data - check nested structure
      let constituentId = data.constituent_id;
      
      // If not at root level, check nested structure
      if (!constituentId && data.results?.consumer_behavior?.records?.[0]?.contact_info?.constituent_id) {
        constituentId = data.results.consumer_behavior.records[0].contact_info.constituent_id;
      }
      
      // Only fetch if we have constituent_id, it's a database source, and we haven't already loaded
      const isDatabaseSource = data.source === 'database';
      if (constituentId && isDatabaseSource && !transactionData && !transactionLoading) {
        setTransactionLoading(true);
        try {
          console.log('Auto-fetching transactions for constituent_id:', constituentId);
          const transactionResponse = await getTransactions(constituentId);
          console.log('Transaction response:', transactionResponse);
          
          if (transactionResponse && transactionResponse.transactions) {
            console.log('Transactions found:', transactionResponse.transactions.length);
            setTransactionData(transactionResponse);
          } else {
            console.warn('No transactions in response');
            setTransactionData({ transactions: [], total_count: 0 });
          }
        } catch (error) {
          console.error('Transaction fetch failed:', error);
          setTransactionData({ 
            transactions: [], 
            total_count: 0,
            error: error instanceof Error ? error.message : 'Failed to fetch transactions'
          });
        } finally {
          setTransactionLoading(false);
        }
      }
    };

    fetchTransactionsAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]); // Re-run when data changes

  // Auto-fetch philanthropy data when search criteria is available
  React.useEffect(() => {
    const fetchPhilanthropyAuto = async () => {
      // Extract first and last name from search criteria or data
      let firstName = searchCriteria?.FIRST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.first_name || '';
      let lastName = searchCriteria?.LAST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.last_name || '';
      let city = searchCriteria?.CITY || data?.results?.personal_info?.records?.[0]?.contact_info?.city || '';
      let state = searchCriteria?.STATE || data?.results?.personal_info?.records?.[0]?.contact_info?.state || '';
      
      // Only fetch if we have the required info and haven't already loaded
      if (firstName && lastName && city && state && !philanthropyData && !philanthropyLoading) {
        setPhilanthropyLoading(true);
        try {
          console.log(`Auto-fetching philanthropy data for ${firstName} ${lastName} from ${city}, ${state}`);
          const philanthropyResponse = await getPhilanthropy(
            `${firstName} ${lastName}`.trim(),
            city,
            state
          );
          console.log('Philanthropy response:', philanthropyResponse);
          setPhilanthropyData(philanthropyResponse);
        } catch (error) {
          console.error('Philanthropy fetch failed:', error);
          setPhilanthropyData({
            success: false,
            records: [],
            error: error instanceof Error ? error.message : 'Failed to fetch philanthropy data'
          });
        } finally {
          setPhilanthropyLoading(false);
        }
      }
    };

    fetchPhilanthropyAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchCriteria]);

  // Poll for philanthropy data updates every 3 minutes while loading
  React.useEffect(() => {
    if (!philanthropyLoading) return; // Only poll while loading

    const pollInterval = setInterval(async () => {
      try {
        let firstName = searchCriteria?.FIRST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.first_name || '';
        let lastName = searchCriteria?.LAST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.last_name || '';
        let city = searchCriteria?.CITY || data?.results?.personal_info?.records?.[0]?.contact_info?.city || '';
        let state = searchCriteria?.STATE || data?.results?.personal_info?.records?.[0]?.contact_info?.state || '';
        
        if (firstName && lastName && city && state) {
          console.log(`Polling philanthropy data for ${firstName} ${lastName}...`);
          const philanthropyResponse = await getPhilanthropy(
            `${firstName} ${lastName}`.trim(),
            city,
            state
          );
          
          // Only update if we got new data or an error
          if (philanthropyResponse) {
            console.log('Philanthropy poll update:', philanthropyResponse);
            setPhilanthropyData(philanthropyResponse);
            
            // If data is ready (success and has records), stop polling
            if (philanthropyResponse.success && philanthropyResponse.records && philanthropyResponse.records.length > 0) {
              setPhilanthropyLoading(false);
            }
          }
        }
      } catch (error) {
        console.error('Philanthropy poll failed:', error);
      }
    }, 3 * 60 * 1000); // Poll every 3 minutes

    return () => clearInterval(pollInterval);
  }, [philanthropyLoading, searchCriteria, data]);

  // Auto-fetch AI insights for all categories when search completes
  React.useEffect(() => {
    const fetchAiInsightsAuto = async () => {
      if (!AI_ANALYSIS_ENABLED) return; // KnowledgeCore AI Analysis disabled

      // Only auto-fetch if we haven't already fetched insights for any category
      const hasAnyInsights = Object.keys(aiInsightsResults).length > 0;
      if (hasAnyInsights) {
        return; // Already fetched
      }

      // Extract profile data from search results
      let firstName = searchCriteria?.FIRST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.first_name || '';
      let lastName = searchCriteria?.LAST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.last_name || '';
      let city = searchCriteria?.CITY || data?.results?.personal_info?.records?.[0]?.contact_info?.city || '';
      let state = searchCriteria?.STATE || data?.results?.personal_info?.records?.[0]?.contact_info?.state || '';
      
      // Only fetch if we have required data
      if (!firstName || !lastName) {
        return;
      }

      // Build profile data
      const profileData: any = {
        first_name: firstName,
        last_name: lastName,
        city: city,
        state: state,
        street: searchCriteria?.STREET1 || '',
        zip: searchCriteria?.ZIP || ''
      };

      // Add all available data from the search results
      if (data) {
        Object.keys(data).forEach(key => {
          profileData[key] = data[key];
        });
      }

      profileData.source = 'Knowledge Core IQ Search';
      profileData.search_timestamp = new Date().toISOString();

      // Fetch all AI insight categories in parallel
      const aiCategories = [
        'Profile',
        'Financial',
        'News',
        'Charitable Activities',
        'Political Interests',
        'Social Media',
        'Consumer Behavior',
        'Affiliations'
      ];

      console.log(`Auto-fetching AI insights for all categories for ${firstName} ${lastName}`);
      
      try {
        const insightPromises = aiCategories.map(async (category) => {
          try {
            setAiInsightsLoading(prev => ({ ...prev, [category]: true }));
            const response = await generateAIInsights(category, profileData);
            
            if (response.ai_insights && response.ai_insights.insights) {
              console.log(`Auto-fetched AI insights for ${category}`);
              return { category, insights: response.ai_insights.insights };
            } else {
              return { category, insights: "AI insights could not be generated at this time. Please try again later." };
            }
          } catch (error) {
            console.error(`Failed to auto-fetch ${category} insights:`, error);
            return { category, insights: `AI insights temporarily unavailable: ${error instanceof Error ? error.message : 'Unknown error'}` };
          } finally {
            setAiInsightsLoading(prev => ({ ...prev, [category]: false }));
          }
        });

        const results = await Promise.all(insightPromises);
        
        // Update all insights at once
        const newInsights: { [key: string]: string } = {};
        results.forEach(result => {
          newInsights[result.category] = result.insights;
        });
        
        setAiInsightsResults(newInsights);
        console.log('All AI insights auto-fetched successfully');
      } catch (error) {
        console.error('Auto-fetch AI insights failed:', error);
      }
    };

    fetchAiInsightsAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchCriteria]);

  // Auto-fetch biographical summary when data changes
  React.useEffect(() => {
    const fetchBiographicalSummaryAuto = async () => {
      // Don't re-fetch if we already have a summary for this data
      if (biologicalSummary) {
        return;
      }

      // Extract profile data from search results
      let firstName = searchCriteria?.FIRST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.first_name || '';
      let lastName = searchCriteria?.LAST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.last_name || '';
      
      // Only fetch if we have required data
      if (!firstName || !lastName) {
        return;
      }

      // Build profile data - include all data so the Profile prompt can extract its data points
      const profileData = buildAIProfileData(data, searchCriteria);

      try {
        setBiologicalSummaryLoading(true);
        console.log(`Auto-fetching biographical summary for ${firstName} ${lastName}`);
        const response = await generateAIInsights('Profile', profileData);
        
        if (response.ai_insights && response.ai_insights.insights) {
          console.log(`Auto-fetched biographical summary`);
          setBiologicalSummary(response.ai_insights.insights);
        } else {
          setBiologicalSummary("Biographical summary could not be generated at this time.");
        }
      } catch (error) {
        console.error(`Failed to auto-fetch biographical summary:`, error);
        setBiologicalSummary(`Biographical summary temporarily unavailable: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setBiologicalSummaryLoading(false);
      }
    };

    fetchBiographicalSummaryAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchCriteria]);

  // Auto-fetch consumer behavior summary when data changes
  React.useEffect(() => {
    const fetchConsumerBehaviorSummaryAuto = async () => {
      // Don't re-fetch if we already have a summary for this data
      if (consumerBehaviorSummary) {
        return;
      }

      // Only fetch if we have data at all
      if (!data) {
        return;
      }

      // Build profile data - include all data to extract consumer behavior insights
      let firstName = searchCriteria?.FIRST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.first_name || '';
      let lastName = searchCriteria?.LAST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.last_name || '';

      const profileData: any = {
        first_name: firstName,
        last_name: lastName
      };

      // Add all available data from the search results
      if (data) {
        Object.keys(data).forEach(key => {
          profileData[key] = data[key];
        });
      }

      profileData.source = 'Knowledge Core IQ Search';
      profileData.search_timestamp = new Date().toISOString();

      try {
        setConsumerBehaviorSummaryLoading(true);
        console.log(`Auto-fetching consumer behavior summary`);
        const response = await generateAIInsights('Consumer Behavior Summary', profileData);
        
        if (response.ai_insights && response.ai_insights.insights) {
          console.log(`Auto-fetched consumer behavior summary`);
          setConsumerBehaviorSummary(response.ai_insights.insights);
        } else {
          setConsumerBehaviorSummary("Consumer behavior summary could not be generated at this time.");
        }
      } catch (error) {
        console.error(`Failed to auto-fetch consumer behavior summary:`, error);
        setConsumerBehaviorSummary(`Consumer behavior summary temporarily unavailable: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setConsumerBehaviorSummaryLoading(false);
      }
    };

    fetchConsumerBehaviorSummaryAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchCriteria]);

  // Auto-fetch financial summary when data changes
  React.useEffect(() => {
    const fetchFinancialSummaryAuto = async () => {
      // Don't re-fetch if we already have a summary for this data
      if (financialSummary) {
        return;
      }

      // Only fetch if we have data at all
      if (!data) {
        return;
      }

      // Build profile data - include all data to extract financial insights
      let firstName = searchCriteria?.FIRST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.first_name || '';
      let lastName = searchCriteria?.LAST_NAME || data?.results?.personal_info?.records?.[0]?.contact_info?.last_name || '';

      const profileData: any = {
        first_name: firstName,
        last_name: lastName
      };

      // Add all available data from the search results
      if (data) {
        Object.keys(data).forEach(key => {
          profileData[key] = data[key];
        });
      }

      profileData.source = 'Knowledge Core IQ Search';
      profileData.search_timestamp = new Date().toISOString();

      try {
        setFinancialSummaryLoading(true);
        console.log(`Auto-fetching financial summary`);
        const response = await generateAIInsights('Financial Summary', profileData);
        
        if (response.ai_insights && response.ai_insights.insights) {
          console.log(`Auto-fetched financial summary`);
          setFinancialSummary(response.ai_insights.insights);
        } else {
          setFinancialSummary("Financial summary could not be generated at this time.");
        }
      } catch (error) {
        console.error(`Failed to auto-fetch financial summary:`, error);
        setFinancialSummary(`Financial summary temporarily unavailable: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setFinancialSummaryLoading(false);
      }
    };

    fetchFinancialSummaryAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchCriteria]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };



  const handleAiInsights = async (category: string) => {
    if (!AI_ANALYSIS_ENABLED) return; // KnowledgeCore AI Analysis disabled
    setAiInsightsLoading(prev => ({ ...prev, [category]: true }));
    
    try {
      // Create profile data from search criteria and results
      const profileData: any = {};
      
      // Add search criteria (name, city, state) - this is what we need for AI prompts
      if (searchCriteria) {
        profileData.FIRST_NAME = searchCriteria.FIRST_NAME;
        profileData.LAST_NAME = searchCriteria.LAST_NAME;
        profileData.CITY = searchCriteria.CITY;
        profileData.STATE = searchCriteria.STATE;
      }
      
      // Add all available data from the search results
      if (data) {
        Object.keys(data).forEach(key => {
          profileData[key] = data[key];
        });
      }
      
      // Add metadata
      profileData.source = 'Knowledge Core IQ Search';
      profileData.search_timestamp = new Date().toISOString();
      
      console.log(`Generating AI insights for ${category}:`, profileData);
      
      // Call the AI insights API
      const response = await generateAIInsights(category, profileData);
      
      console.log(`Full AI insights response for ${category}:`, JSON.stringify(response, null, 2));
      console.log(`Response.ai_insights:`, response.ai_insights);
      console.log(`Response.ai_insights.insights:`, response.ai_insights?.insights);
      console.log(`Insights text is empty? ${!response.ai_insights?.insights}`);
      
      if (response.ai_insights && response.ai_insights.insights) {
        setAiInsightsResults(prev => ({ 
          ...prev, 
          [category]: response.ai_insights.insights
        }));
      } else {
        setAiInsightsResults(prev => ({ 
          ...prev, 
          [category]: "AI insights could not be generated at this time. Please try again later."
        }));
      }
    } catch (error) {
      console.error('AI insights generation failed:', error);
      setAiInsightsResults(prev => ({ 
        ...prev, 
        [category]: `AI insights temporarily unavailable: ${error instanceof Error ? error.message : 'Unknown error'}`
      }));
    } finally {
      setAiInsightsLoading(prev => ({ ...prev, [category]: false }));
    }
  };

  const handlePhoneValidation = async () => {
    if (!searchCriteria) {
      console.warn('No search criteria available for phone validation');
      return;
    }

    setPhoneValidationLoading(true);
    try {
      console.log('Manual phone validation requested...');
      const phoneData = await validatePhoneNumbers(searchCriteria);
      console.log('Raw phone validation response:', phoneData);
      
      if (phoneData && phoneData.phone_validation) {
        console.log('Phone validation data found:', phoneData.phone_validation);
        setPhoneValidationData(phoneData.phone_validation);
        console.log('Phone validation completed');
        
        // Check if there was an error in the response
        if (phoneData.phone_validation.validation_metadata?.error) {
          console.warn('Phone validation API returned error:', phoneData.phone_validation.validation_metadata.error);
        } else {
          console.log('Phone validation successful, total phones:', phoneData.phone_validation.total_phones);
        }
      } else {
        console.warn('No phone_validation data in response:', phoneData);
      }
    } catch (error) {
      console.error('Phone validation request failed:', error);
      
      // Create an error response for display
      setPhoneValidationData({
        phones_found: [],
        mobile_phones: [],
        landline_phones: [],
        dnc_compliant_phones: [],
        non_dnc_phones: [],
        total_phones: 0,
        validation_metadata: {
          error: error instanceof Error ? error.message : 'Network error occurred',
          api_source: 'experian_aperture',
          validation_status: 'network_error'
        }
      });
    } finally {
      setPhoneValidationLoading(false);
    }
  };

  const handleEmailValidation = async () => {
    if (!searchCriteria) {
      console.warn('No search criteria available for email validation');
      return;
    }

    setEmailValidationLoading(true);
    try {
      console.log('Manual email validation requested...');
      const emailData = await validateEmailAddress(searchCriteria);
      console.log('Raw email validation response:', emailData);
      
      if (emailData && emailData.email_validation) {
        console.log('Email validation data found:', emailData.email_validation);
        setEmailValidationData(emailData.email_validation);
        console.log('Email validation completed');
        
        // Check if there was an error in the response
        if (emailData.email_validation.validation_metadata?.error) {
          console.warn('Email validation API returned error:', emailData.email_validation.validation_metadata.error);
        } else {
          console.log('Email validation successful, email found:', emailData.email_validation.email_found);
        }
      } else {
        console.warn('No email_validation data in response:', emailData);
      }
    } catch (error) {
      console.error('Email validation request failed:', error);
      
      // Create an error response for display
      setEmailValidationData({
        email_found: null,
        email_type: null,
        total_emails: 0,
        validation_metadata: {
          error: error instanceof Error ? error.message : 'Network error occurred',
          api_source: 'experian_aperture',
          validation_status: 'network_error'
        }
      });
    } finally {
      setEmailValidationLoading(false);
    }
  };

  const handleFetchTransactions = async () => {
    // Get constituent_id from data - check nested structure
    let constituentId = data.constituent_id;
    
    // If not at root level, check nested structure
    if (!constituentId && data.results?.consumer_behavior?.records?.[0]?.contact_info?.constituent_id) {
      constituentId = data.results.consumer_behavior.records[0].contact_info.constituent_id;
    }
    
    if (!constituentId) {
      console.warn('No constituent_id available for fetching transactions');
      setTransactionData({ 
        transactions: [], 
        total_count: 0,
        error: 'No constituent ID found in search results'
      });
      return;
    }

    setTransactionLoading(true);
    try {
      console.log('Fetching transactions for constituent_id:', constituentId);
      const transactionResponse = await getTransactions(constituentId);
      console.log('Transaction response:', transactionResponse);
      
      if (transactionResponse && transactionResponse.transactions) {
        console.log('Transactions found:', transactionResponse.transactions.length);
        setTransactionData(transactionResponse);
      } else {
        console.warn('No transactions in response');
        setTransactionData({ transactions: [], total_count: 0 });
      }
    } catch (error) {
      console.error('Transaction fetch failed:', error);
      setTransactionData({ 
        transactions: [], 
        total_count: 0,
        error: error instanceof Error ? error.message : 'Failed to fetch transactions'
      });
    } finally {
      setTransactionLoading(false);
    }
  };

  // Helper function to format field names for display
  const formatFieldName = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to format values for display
  const formatValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined || value === '') {
      return <Typography variant="body2" color="text.secondary">N/A</Typography>;
    }
    
    if (typeof value === 'boolean') {
      return (
        <Chip
          label={value ? 'Yes' : 'No'}
          size="small"
          color={value ? 'success' : 'default'}
          variant="outlined"
        />
      );
    }
    
    if (typeof value === 'object') {
      return (
        <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
          {JSON.stringify(value, null, 2)}
        </Typography>
      );
    }
    
    return <Typography variant="body2">{String(value)}</Typography>;
  };

  // Helper function to check if a field should be rendered as a gauge and get its max value
  const getGaugeConfig = (fieldName: string): { isGauge: boolean; label: string; maxValue: number; valueDirection?: string } | null => {
    const lowerFieldName = fieldName.toLowerCase();
    
    const gaugeFields: Record<string, { label: string; maxValue: number; valueDirection?: string }> = {
      'affluence score': { label: 'Affluence Score', maxValue: 99999, valueDirection: 'Lower is Better' },
      'iq: wealthiq net assets score': { label: 'IQ: WealthIQ Net Assets Score', maxValue: 99999, valueDirection: 'Lower is Better' },
      'overall financial health score': { label: 'Overall Financial Health Score', maxValue: 99999, valueDirection: 'Higher is Better' },
    };
    
    for (const [key, config] of Object.entries(gaugeFields)) {
      if (lowerFieldName.includes(key)) {
        return { isGauge: true, ...config };
      }
    }
    
    return null;
  };

  // Function to categorize fields based on patterns in field names
  const categorizeFields = (obj: any): Record<string, Array<[string, any]>> => {
    const categories = {
      'Consumer Behavior': [] as Array<[string, any]>,
      'Profile': [] as Array<[string, any]>,
      'Financial': [] as Array<[string, any]>,
      'Political Interests': [] as Array<[string, any]>,
      'Contact Validation': [] as Array<[string, any]>,
      'Philanthropy': [] as Array<[string, any]>,
      'Affiliations': [] as Array<[string, any]>,
      'Social Media': [] as Array<[string, any]>,
      'News': [] as Array<[string, any]>,
      'Scores': [] as Array<[string, any]>,
      'Suggested Ask': [] as Array<[string, any]>,
    };

    const flattenObject = (obj: any, prefix = ''): Array<[string, any]> => {
      const result: Array<[string, any]> = [];
      
      for (const [key, value] of Object.entries(obj)) {
        const displayKey = key;
        
        // Skip empty, null, or undefined values
        if (value === null || value === undefined || value === '' || 
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'object' && value !== null && Object.keys(value).length === 0)) {
          continue;
        }
        
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          const nested = flattenObject(value, '');
          result.push(...nested);
        } else {
          result.push([displayKey, value]);
        }
      }
      
      return result;
    };

    const flattenedData = flattenObject(obj);

    // Categorize each field based on keywords in field names
    flattenedData.forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();
      
      // Act/Int: prefix fields go to Consumer Behavior
      if (lowerKey.startsWith('act/int:') || lowerKey.includes('act/int:')) {
        categories['Consumer Behavior'].push([key, value]);
      }
      // HH: prefix fields go to Consumer Behavior
      else if (lowerKey.startsWith('hh:') || lowerKey.includes('hh:lifestyle') || lowerKey.includes('hh:')) {
        categories['Consumer Behavior'].push([key, value]);
      }
      // Property/Asset fields go to Financial
      else if (
        lowerKey.includes('home land value') ||
        lowerKey.includes('home total value') ||
        lowerKey.includes('home: home purchase price') ||
        lowerKey.includes('home: purchase date') ||
        lowerKey.includes('home: swimming pool') ||
        lowerKey.includes('length of residence')
      ) {
        categories['Financial'].push([key, value]);
      }
      // Demographic/Profile fields (move specific geo and address fields)
      else if (
        lowerKey.includes('children by age/gender') ||
        lowerKey.includes('number of children in household') ||
        lowerKey.includes('zipplus') ||
        lowerKey.includes('fallback source') ||
        lowerKey.includes('database checked') ||
        lowerKey.includes('database records found') ||
        lowerKey.includes('mobile count') ||
        lowerKey.includes('landline count') ||
        lowerKey.includes('dnc compliant count') ||
        lowerKey.includes('non dnc count') ||
        lowerKey.includes('api source') ||
        lowerKey.includes('validation date') ||
        lowerKey.includes('validation status') ||
        lowerKey.includes('predir') ||
        lowerKey.includes('prim name') ||
        lowerKey.includes('prim range') ||
        lowerKey.includes('real estate tax') ||
        lowerKey.includes('suffix') ||
        lowerKey.includes('suitablefordelivery') ||
        lowerKey.includes('transaction id') ||
        lowerKey.includes('carrier route') ||
        lowerKey.includes('checkdigit') ||
        lowerKey.includes('city name') ||
        lowerKey.includes('country') ||
        lowerKey.includes('county') ||
        lowerKey.includes('county code') ||
        lowerKey.includes('data type') ||
        lowerKey.includes('dpbc') ||
        lowerKey.includes('fipscode') ||
        lowerKey.includes('geo blk') ||
        lowerKey.includes('geo lat') ||
        lowerKey.includes('geo lng') ||
        lowerKey.includes('geo msa') ||
        lowerKey.includes('geomatch')
      ) {
        categories['Profile'].push([key, value]);
      }
      // Credit-related fields go to Financial
      else if (
        lowerKey.includes('avg mrgg type') ||
        lowerKey.includes('avg rvlv bnkcd') ||
        lowerKey.includes('avg mrgg type trd') ||
        lowerKey.includes('avg rvlv bnkcd trd') ||
        lowerKey.includes('average number of external collection trades') ||
        lowerKey.includes('average number of installment trades') ||
        lowerKey.includes('average number of revolving trades')
      ) {
        categories['Financial'].push([key, value]);
      }
      // Contact Validation patterns (check first to avoid conflicts)
      else if (
        lowerKey.includes('phone_validation') ||
        lowerKey.includes('phones_found') ||
        lowerKey.includes('mobile_phones') ||
        lowerKey.includes('landline_phones') ||
        lowerKey.includes('dnc_compliant_phones') ||
        lowerKey.includes('non_dnc_phones') ||
        lowerKey.includes('email_validation') ||
        lowerKey.includes('email_found') ||
        lowerKey.includes('email_type') ||
        lowerKey.includes('validation_metadata') ||
        (lowerKey.includes('phone') && (
          lowerKey.includes('validation') ||
          lowerKey.includes('found') ||
          lowerKey.includes('mobile') ||
          lowerKey.includes('landline') ||
          lowerKey.includes('dnc') ||
          lowerKey.includes('rank') ||
          lowerKey.includes('status')
        )) ||
        (lowerKey.includes('email') && (
          lowerKey.includes('validation') ||
          lowerKey.includes('found') ||
          lowerKey.includes('type')
        ))
      ) {
        categories['Contact Validation'].push([key, value]);
      }
      // Suggested Ask patterns
      else if (
        lowerKey.includes('low ask') ||
        lowerKey.includes('mid ask') ||
        lowerKey.includes('high ask')
      ) {
        categories['Suggested Ask'].push([key, value]);
      }
      // Scores patterns (reasoning, key_factors, score fields, etc.)
      else if (
        lowerKey.includes('reasoning') ||
        lowerKey.includes('key_factors') ||
        lowerKey.includes('key factors') ||
        lowerKey === 'capacity score' ||
        lowerKey === 'propensity score' ||
        lowerKey === 'planned giving score' ||
        lowerKey === 'planned gift types' ||
        lowerKey === 'overall score' ||
        (lowerKey.includes('score') && (lowerKey.includes('reason') || lowerKey.includes('factor')))
      ) {
        categories['Scores'].push([key, value]);
      }
      // DSE fields (spending patterns) go to Financial
      else if (
        lowerKey.includes('dse:')
      ) {
        categories['Financial'].push([key, value]);
      }
      // Consumer Behavior patterns
      else if (
        lowerKey.includes('acty/int:') ||
        lowerKey.includes('activity') ||
        lowerKey.includes('interest') ||
        lowerKey.includes('hobby') ||
        lowerKey.includes('lifestyle') ||
        lowerKey.includes('truetouch') ||
        lowerKey.includes('behavior') ||
        lowerKey.includes('shopping') ||
        lowerKey.includes('buying') ||
        lowerKey.includes('sports') ||
        lowerKey.includes('music') ||
        lowerKey.includes('travel') ||
        lowerKey.includes('entertainment') ||
        lowerKey.includes('magazine') ||
        lowerKey.includes('reading') ||
        lowerKey.includes('cooking') ||
        lowerKey.includes('crafts') ||
        lowerKey.includes('collecting') ||
        lowerKey.includes('pets') ||
        lowerKey.includes('health/fitness') ||
        lowerKey.includes('fitness') ||
        lowerKey.includes('technology') ||
        lowerKey.includes('electronics') ||
        lowerKey.includes('internet') ||
        lowerKey.includes('computer') ||
        lowerKey.includes('gaming') ||
        lowerKey.includes('video game') ||
        lowerKey.includes('streaming') ||
        lowerKey.includes('outdoor') ||
        lowerKey.includes('recreation') ||
        lowerKey.includes('sweepstakes') ||
        lowerKey.includes('lottery')
      ) {
        categories['Consumer Behavior'].push([key, value]);
      }
      // Political Interests patterns
      else if (
        lowerKey.includes('political') ||
        lowerKey.includes('conservative') ||
        lowerKey.includes('liberal') ||
        lowerKey.includes('voting') ||
        lowerKey.includes('government') ||
        lowerKey.includes('military') ||
        lowerKey.includes('veteran')
      ) {
        categories['Political Interests'].push([key, value]);
      }
      // Philanthropy patterns (Donor flags for giving categories of interest)
      else if (
        (lowerKey.includes('donor_') && lowerKey.includes('_flag')) ||
        lowerKey.includes('animal_welfare_flag') ||
        lowerKey.includes('arts_cultural_flag')
      ) {
        categories['Philanthropy'].push([key, value]);
      }
      // Charitable Activities patterns - now included in Philanthropy
      else if (
        lowerKey.includes('socl caus/con') ||
        lowerKey.includes('social cause') ||
        lowerKey.includes('charitable') ||
        lowerKey.includes('charity') ||
        lowerKey.includes('donation') ||
        lowerKey.includes('volunteer') ||
        lowerKey.includes('animal wf') ||
        lowerKey.includes('environment') ||
        lowerKey.includes('wildlife') ||
        lowerKey.includes('children') ||
        lowerKey.includes('veterans') ||
        (lowerKey.includes('health') && lowerKey.includes('cause'))
      ) {
        categories['Philanthropy'].push([key, value]);
      }
      // Financial patterns
      else if (
        lowerKey.includes('financial') ||
        lowerKey.includes('credit') ||
        lowerKey.includes('invest') ||
        lowerKey.includes('income') ||
        lowerKey.includes('mortgage') ||
        lowerKey.includes('loan') ||
        lowerKey.includes('bank') ||
        lowerKey.includes('equity') ||
        lowerKey.includes('asset') ||
        lowerKey.includes('wealth') ||
        lowerKey.includes('affluence') ||
        (lowerKey.includes('estimated') && (lowerKey.includes('home') || lowerKey.includes('value'))) ||
        (lowerKey.includes('property') && lowerKey.includes('value')) ||
        lowerKey.includes('card') ||
        lowerKey.includes('insurance') ||
        lowerKey.includes('retirement') ||
        lowerKey.includes('mutual fund') ||
        lowerKey.includes('stock') ||
        lowerKey.includes('bond') ||
        lowerKey.includes('ira') ||
        lowerKey.includes('money') ||
        lowerKey.includes('payment')
      ) {
        categories['Financial'].push([key, value]);
      }
      // Demographic patterns (everything else demographic-related)
      else if (
        lowerKey.includes('age') ||
        lowerKey.includes('birth') ||
        lowerKey.includes('gender') ||
        lowerKey.includes('marital') ||
        lowerKey.includes('education') ||
        lowerKey.includes('ethnicity') ||
        lowerKey.includes('household') ||
        lowerKey.includes('children') ||
        lowerKey.includes('family') ||
        lowerKey.includes('address') ||
        lowerKey.includes('location') ||
        lowerKey.includes('geography') ||
        lowerKey.includes('dwelling') ||
        lowerKey.includes('homeowner') ||
        lowerKey.includes('renter') ||
        lowerKey.includes('property type') ||
        lowerKey.includes('bedrooms') ||
        lowerKey.includes('year built') ||
        lowerKey.includes('mail') ||
        lowerKey.includes('phone') ||
        lowerKey.includes('email') ||
        lowerKey.includes('person #') ||
        lowerKey.includes('resident') ||
        lowerKey.includes('occupation') ||
        lowerKey.includes('business owner') ||
        lowerKey.includes('new mover')
      ) {
        categories['Profile'].push([key, value]);
      }
      // Default to Consumer Behavior for unmatched fields
      else {
        categories['Consumer Behavior'].push([key, value]);
      }
    });

    return categories;
  };

  const allCategorizedData = categorizeFields(data);
  const categorizedData = allCategorizedData;
  
  // Add DataIris Philanthropy fields to categorizedData for tab count
  if (datairisData?.results?.['Philanthropy']?.['Giving Categories of Interest']) {
    const philanthropyFields = datairisData.results['Philanthropy']['Giving Categories of Interest'];
    if (Array.isArray(philanthropyFields)) {
      categorizedData['Philanthropy'] = philanthropyFields.map((item: any) => [item.label, item.value]);
    }
  }

  // Define sub-sections for each category
  const getSubSections = (category: string, fields: Array<[string, any]>) => {
    const subSections: { [key: string]: Array<[string, any]> } = {};
    
    switch (category) {
      case 'Profile':
        // Extract DataIris Profile data if available
        const datairisProfile = datairisData?.results?.['Profile']?.['Overview'];
        
        // Debug logging
        console.log('DataIris full data:', datairisData);
        console.log('DataIris results:', datairisData?.results);
        console.log('DataIris Profile:', datairisData?.results?.['Profile']);
        console.log('DataIris Profile Overview:', datairisProfile);
        
        // Extract individual values from DataIris
        let estimatedIncome = 'No information available for this user';
        let homeMarketValue = 'No information available for this user';
        let netWorth = 'No information available for this user';
        let capacityRange = 'No information available for this user';
        
        if (datairisProfile && Array.isArray(datairisProfile)) {
          const incomeItem = datairisProfile.find((item: any) => item.label === 'Estimated Household Income');
          const homeItem = datairisProfile.find((item: any) => item.label === 'Home Market Value');
          const netWorthItem = datairisProfile.find((item: any) => item.label === 'Net Worth');
          const capacityItem = datairisProfile.find((item: any) => item.label === 'Capacity Range $');
          
          estimatedIncome = incomeItem?.value || 'No information available for this user';
          homeMarketValue = homeItem?.value || 'No information available for this user';
          netWorth = netWorthItem?.value || 'No information available for this user';
          capacityRange = capacityItem?.value || 'No information available for this user';
          
          console.log('Found DataIris values:', { estimatedIncome, homeMarketValue, netWorth, capacityRange });
        } else if (datairisData) {
          // Try alternative structure
          console.log('DataIris data type:', typeof datairisProfile, 'Is Array?', Array.isArray(datairisProfile));
        }
        
        // Initialize Biographical first (moved to be first tab)
        subSections['Biographical'] = [];
        
        fields.forEach(([key, value]) => {
          const lowerKey = key.toLowerCase();
          if (!lowerKey.includes('donation') && !lowerKey.includes('giving') && !lowerKey.includes('charity') && !lowerKey.includes('location')) {
            subSections['Biographical'].push([key, value]);
          }
        });

        // Add predefined Overview Section tiles (second tab)
        subSections['Overview Section'] = [
          ['Capacity Range $', capacityRange],
          ['Total Political Giving $', 'Subscribe For Additional Data'],
          ['Charitable Giving $', 'Subscribe For Additional Data'],
          ['Estimated Household Income', estimatedIncome],
          ['Home Market Value', homeMarketValue],
          ['Net Worth', netWorth]
        ];
        
        // Check if we have transaction data or if it's loading/available (third tab)
        const hasConstituentId = data.constituent_id || data.results?.consumer_behavior?.records?.[0]?.contact_info?.constituent_id;
        const isDatabaseSource = data.source === 'database';
        
        if (transactionLoading) {
          // Show loading state
          subSections['Giving History'] = [
            ['Status', 'Loading transaction history...']
          ];
        } else if (transactionData && transactionData.transactions && transactionData.transactions.length > 0) {
          // Display transaction data in table format
          subSections['Giving History'] = [
            ['__TRANSACTION_TABLE__', JSON.stringify(transactionData.transactions)]
          ];
        } else if (transactionData && transactionData.transactions && transactionData.transactions.length === 0) {
          // No transactions found
          subSections['Giving History'] = [
            ['Status', 'No transaction history found for this constituent']
          ];
        } else if (!hasConstituentId || !isDatabaseSource) {
          // No constituent_id available
          subSections['Giving History'] = [
            ['Status', 'Transaction history only available for database records'],
            ['Note', 'Search using the database to view giving history']
          ];
        } else {
          // Default state (shouldn't reach here normally)
          subSections['Giving History'] = [
            ['Status', 'Transaction history loading...']
          ];
        }
        break;
        
      case 'Consumer Behavior':
        // Filter out Data Source, Match Types, Records, and Source fields
        // Also deduplicate First Name and Last Name (keep only first occurrence)
        // Remove specific field sections: Contact, Overview, Home, Giving Categories Of Interest, Unmapped, Hobbies, Arts & Culture, Lifestyle, Reasoning, Ask fields
        const seenKeys = new Set<string>();
        const filteredConsumerBehavior = fields.filter(([key]) => {
          const lowerKey = key.toLowerCase().trim();
          // Check if field should be excluded
          const isExcluded = 
            lowerKey.includes('source') || 
            lowerKey.includes('match') || 
            lowerKey.includes('records') ||
            lowerKey === 'contact' ||
            lowerKey === 'overview' ||
            lowerKey === 'home' ||
            lowerKey === 'giving categories of interest' ||
            lowerKey === 'unmapped' ||
            lowerKey === 'hobbies' ||
            lowerKey === 'arts & culture' ||
            lowerKey === 'lifestyle' ||
            lowerKey.includes('reasoning') ||
            lowerKey === 'low ask' ||
            lowerKey === 'mid ask' ||
            lowerKey === 'high ask' ||
            (lowerKey.includes('ask') && (lowerKey.includes('low') || lowerKey.includes('mid') || lowerKey.includes('high')));
          
          if (isExcluded) {
            return false;
          }
          
          // Normalize key for deduplication (remove spaces and underscores)
          const normalizedKey = lowerKey.replace(/[\s_]/g, '');
          
          // Check for duplicates of first name or last name
          if (normalizedKey.includes('firstname') || normalizedKey.includes('lastname')) {
            if (seenKeys.has(normalizedKey)) {
              return false; // Skip duplicate
            }
            seenKeys.add(normalizedKey);
          }
          
          return true;
        });
        subSections['Consumer Behavior'] = filteredConsumerBehavior;
        break;
        
      case 'Financial':
        subSections['Wealth Analysis'] = [];
        subSections['Assets'] = [];
        subSections['Donor Advised Funds'] = [];
        subSections['Foundation-Personal/Public'] = [];
        
        fields.forEach(([key, value]) => {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes('hh:') || lowerKey.startsWith('hh:')) {
            // Move HH prefix fields to Consumer Behavior (handled in categorizeFields)
            // These should not appear in Financial
          } else if (lowerKey.includes('invest:') || lowerKey.startsWith('invest:')) {
            subSections['Assets'].push([key, value]);
          } else if (lowerKey.includes('wealth') || lowerKey.includes('income') || lowerKey.includes('credit')) {
            subSections['Wealth Analysis'].push([key, value]);
          } else if (lowerKey.includes('asset') || lowerKey.includes('property') || lowerKey.includes('home')) {
            subSections['Assets'].push([key, value]);
          } else if (lowerKey.includes('fund') || lowerKey.includes('daf')) {
            subSections['Donor Advised Funds'].push([key, value]);
          } else if (lowerKey.includes('foundation')) {
            subSections['Foundation-Personal/Public'].push([key, value]);
          } else {
            subSections['Wealth Analysis'].push([key, value]);
          }
        });
        break;
        
      case 'Political Interests':
        subSections['Political Interests'] = fields;
        
        // FEC Contributions Section
        if (fecContributionsData && fecContributionsData.success && fecContributionsData.contributions && fecContributionsData.contributions.length > 0) {
          // Store as special marker for table rendering
          const tableRows: Array<[string, any]> = [
            ['__FEC_CONTRIBUTIONS_TABLE__', JSON.stringify(fecContributionsData.contributions)]
          ];
          subSections['Political Contributions'] = tableRows;
        } else if (fecContributionsData && fecContributionsData.success && (!fecContributionsData.contributions || fecContributionsData.contributions.length === 0)) {
          subSections['Political Contributions'] = [
            ['Status', 'No FEC contributions found for this person']
          ];
        } else if (fecContributionsData && fecContributionsData.error) {
          subSections['Political Contributions'] = [
            ['Status', 'Error loading FEC contributions'],
            ['Error Message', fecContributionsData.error]
          ];
        } else {
          subSections['Political Contributions'] = [
            ['Status', 'Loading FEC contributions...']
          ];
        }
        break;
        


      case 'Contact Validation':
        // Use either the existing phone validation data or manually fetched data
        const phoneValidation = data.phone_validation || phoneValidationData;
        console.log('Contact Validation - data.phone_validation:', data.phone_validation);
        console.log('Contact Validation - phoneValidationData:', phoneValidationData);
        console.log('Contact Validation - final phoneValidation:', phoneValidation);
        
        if (phoneValidation) {
          // Create a single table with all phone validation data
          const allPhones: any[] = [];
          
          // Combine mobile phones
          if (phoneValidation.mobile_phones && phoneValidation.mobile_phones.length > 0) {
            phoneValidation.mobile_phones.forEach((phone: any) => {
              allPhones.push({
                type: 'Mobile',
                number: phone.number,
                dnc_status: phone.dnc_status ? 'Yes' : 'No',
                dnc_date: phone.dnc_date || 'N/A',
                rank: phone.rank
              });
            });
          }
          
          // Combine landline phones
          if (phoneValidation.landline_phones && phoneValidation.landline_phones.length > 0) {
            phoneValidation.landline_phones.forEach((phone: any) => {
              allPhones.push({
                type: 'Landline',
                number: phone.number,
                dnc_status: phone.dnc_status ? 'Yes' : 'No',
                dnc_date: phone.dnc_date || 'N/A',
                rank: phone.rank
              });
            });
          }
          
          // Sort by rank (priority)
          allPhones.sort((a, b) => a.rank - b.rank);
          
          // Store phone data for custom table rendering
          if (allPhones.length > 0) {
            subSections['Phone Numbers'] = [
              ['__PHONE_TABLE__', JSON.stringify(allPhones)]
            ];
          }
          
          // Add validation status if there's an error
          const metadata = phoneValidation.validation_metadata;
          if (metadata && (metadata.error || metadata.validation_status === 'failed' || metadata.validation_status === 'error')) {
            subSections['Validation Status'] = [
              ['Status', 'Error'],
              ['Error Message', metadata.error || 'Unknown error occurred']
            ];
          }
        } else {
          // Show when no phone validation data is available
          subSections['Phone Validation'] = [
            ['Status', 'No phone data found during search'],
            ['Note', 'Phone validation is automatically performed during search']
          ];
        }

        // Email Validation Section
        const emailValidation = data.email_validation || emailValidationData;
        console.log('Contact Validation - emailValidationData:', emailValidationData);
        console.log('Contact Validation - final emailValidation:', emailValidation);
        
        if (emailValidation && emailValidation.emails_found && emailValidation.emails_found.length > 0) {
          // Create a single table with all email validation data
          const allEmails: any[] = [];
          
          emailValidation.emails_found.forEach((email: any) => {
            allEmails.push({
              type: email.type || 'Unknown',
              address: email.address || email,
              rank: email.rank || 0
            });
          });
          
          // Sort by rank (priority)
          allEmails.sort((a, b) => a.rank - b.rank);
          
          // Store email data for custom table rendering
          if (allEmails.length > 0) {
            subSections['Email Addresses'] = [
              ['__EMAIL_TABLE__', JSON.stringify(allEmails)]
            ];
          }
          
          // Add validation status if there's an error
          const metadata = emailValidation.validation_metadata;
          if (metadata && (metadata.error || metadata.validation_status === 'failed' || metadata.validation_status === 'error')) {
            subSections['Validation Status'] = [
              ['Status', 'Error'],
              ['Error Message', metadata.error || 'Unknown error occurred']
            ];
          }
        } else {
          // Show when no email validation data is available
          subSections['Email Validation'] = [
            ['Status', 'No email data found during search'],
            ['Note', 'Email validation is automatically performed during search']
          ];
          
          // Address verification placeholder
          subSections['Address Verification'] = [
            ['Address Validation', 'Subscribe For Additional Data'],
            ['Address Type', 'Subscribe For Additional Data']
          ];
        }
        break;

      case 'Philanthropy':
        // Add BrightData contributions first with special table marker (first tab)
        // Show data while still generating - will update as more data arrives
        if (philanthropyData?.records && philanthropyData.records.length > 0) {
          // Store as special marker for table rendering
          const tableRows: Array<[string, any]> = [
            ['__CONTRIBUTIONS_TABLE__', JSON.stringify(philanthropyData.records)]
          ];
          if (philanthropyLoading) {
            tableRows.push(['Status', 'Still generating... (additional data being added)']);
          }
          subSections['Contributions to Organizations'] = tableRows;
        } else if (philanthropyLoading) {
          subSections['Contributions to Organizations'] = [
            ['Status', 'Generating... (processing - may take 10+ minutes)']
          ];
        } else {
          subSections['Contributions to Organizations'] = [
            ['Status', 'No contributions found']
          ];
        }
        
        // Separate charitable activities from general philanthropy fields
        const charityFields = fields.filter(([key, value]) => {
          const lowerKey = key.toLowerCase();
          return lowerKey.includes('socl caus/con') || lowerKey.includes('social cause') || 
                 lowerKey.includes('charitable') || lowerKey.includes('charity') || 
                 lowerKey.includes('donation') || lowerKey.includes('volunteer') || 
                 lowerKey.includes('animal wf') || lowerKey.includes('environment') || 
                 lowerKey.includes('wildlife') || lowerKey.includes('children') || 
                 lowerKey.includes('veterans') || (lowerKey.includes('health') && lowerKey.includes('cause'));
        });
        
        const generalPhilanthropyFields = fields.filter(([key, value]) => {
          const lowerKey = key.toLowerCase();
          return !(lowerKey.includes('socl caus/con') || lowerKey.includes('social cause') || 
                 lowerKey.includes('charitable') || lowerKey.includes('charity') || 
                 lowerKey.includes('donation') || lowerKey.includes('volunteer') || 
                 lowerKey.includes('animal wf') || lowerKey.includes('environment') || 
                 lowerKey.includes('wildlife') || lowerKey.includes('children') || 
                 lowerKey.includes('veterans') || (lowerKey.includes('health') && lowerKey.includes('cause')));
        });
        
        // Extract DataIris Philanthropy data if available (second tab)
        const datairisPhilanthropy = datairisData?.results?.['Philanthropy']?.['Giving Categories of Interest'];
        
        if (datairisPhilanthropy && Array.isArray(datairisPhilanthropy)) {
          // Convert DataIris transformed format to [key, value] format
          const philanthropyFields: Array<[string, any]> = datairisPhilanthropy.map((item: any) => [item.label, item.value]);
          subSections['Giving Categories of Interest'] = philanthropyFields;
          console.log('Philanthropy fields from DataIris:', philanthropyFields);
        } else {
          subSections['Giving Categories of Interest'] = generalPhilanthropyFields;
        }
        
        // Add Charitable Activities section (third tab)
        if (charityFields.length > 0) {
          subSections['Charitable Activities'] = charityFields;
        }
        
        // Add AI Insights section last if available
        if (AI_ANALYSIS_ENABLED && aiInsightsResults['Charitable Activities']) {
          subSections['KnowledgeCore AI Analysis'] = [
            ['__AI_INSIGHTS__', 'Charitable Activities']
          ];
        }
        break;

      case 'Affiliations':
        // Business & Professional section with Apollo data
        if (apolloBusinessData && apolloBusinessData.success && apolloBusinessData.professional_data) {
          const prof = apolloBusinessData.professional_data;
          const apolloRows: any[] = [];
          
          // List of fields to exclude from display (internal/metadata/social media)
          const excludeFields = ['id', '__typename', 'linkedin_url', 'facebook_url', 'instagram_url'];
          
          // Helper function to format keys
          const formatKey = (key: string) => {
            return key.replace(/([A-Z])/g, ' $1')
              .replace(/_/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
              .trim();
          };
          
          // Helper function to format values
          const formatValue = (key: string, value: any) => {
            if (value === null || value === undefined) {
              return 'N/A';
            } else if (Array.isArray(value)) {
              if (value.length === 0) {
                return 'N/A';
              } else if (typeof value[0] === 'object') {
                return JSON.stringify(value, null, 2);
              } else {
                return value.join(', ');
              }
            } else if (typeof value === 'object') {
              return JSON.stringify(value, null, 2);
            } else if (key.toLowerCase().includes('linkedin') && value) {
              return `__LINKEDIN_CLICKABLE__${value}`;
            } else if (key.toLowerCase().includes('url') && value && typeof value === 'string') {
              return `__CLICKABLE__${value}`;
            } else {
              return value || 'N/A';
            }
          };
          
          // Iterate through all person fields
          for (const [key, value] of Object.entries(prof)) {
            // Skip organization object - we'll handle it separately
            if (key === 'organization') continue;
            if (excludeFields.includes(key)) continue;
            
            apolloRows.push([formatKey(key), formatValue(key, value)]);
          }
          
          // Add organization fields with prefix
          if (prof.organization && typeof prof.organization === 'object') {
            for (const [key, value] of Object.entries(prof.organization)) {
              if (excludeFields.includes(key)) continue;
              
              const displayKey = `Organization - ${formatKey(key)}`;
              apolloRows.push([displayKey, formatValue(key, value)]);
            }
          }
          
          // Use special marker for table rendering with pagination
          const tableRows: Array<[string, any]> = [
            ['__APOLLO_TABLE__', JSON.stringify(apolloRows)]
          ];
          subSections['Business & Professional'] = tableRows;
        } else if (apolloBusinessData && apolloBusinessData.message) {
          // Display the message if there are no results or multiple matches
          subSections['Business & Professional'] = [
            ['Status', apolloBusinessData.message]
          ];
        } else {
          // Default fallback
          subSections['Business & Professional'] = [
            ['Current Employer', 'Coming Soon'],
            ['Job Title', 'Coming Soon'],
            ['Industry', 'Coming Soon'],
            ['Professional Associations', 'Coming Soon']
          ];
        }
        
        // IRS Form 990 Nonprofit Section
        if (irsForm990Data && irsForm990Data.success && irsForm990Data.nonprofit_records && irsForm990Data.nonprofit_records.length > 0) {
          // Store as special marker for table rendering
          const tableRows: Array<[string, any]> = [
            ['__IRS_FORM990_TABLE__', JSON.stringify(irsForm990Data.nonprofit_records)]
          ];
          subSections['Non-profit'] = tableRows;
        } else if (irsForm990Data && irsForm990Data.success && (!irsForm990Data.nonprofit_records || irsForm990Data.nonprofit_records.length === 0)) {
          subSections['Non-profit'] = [
            ['Status', 'No IRS Form 990 nonprofit records found for this person']
          ];
        } else {
          subSections['Non-profit'] = [
            ['Current Boards', 'Coming Soon'],
            ['Past Boards', 'Coming Soon'],
            ['Committee Memberships', 'Coming Soon']
          ];
        }
        break;

      case 'Social Media':
        // First, add Social Media links from Apollo data if available
        if (apolloBusinessData && apolloBusinessData.success && apolloBusinessData.professional_data) {
          const prof = apolloBusinessData.professional_data;
          
          subSections['Social Media'] = [];
          
          // LinkedIn
          if (prof.linkedin_url) {
            subSections['Social Media'].push(['LinkedIn', `__CLICKABLE__${prof.linkedin_url}`]);
          } else {
            subSections['Social Media'].push(['LinkedIn', 'Not available for this donor']);
          }
          
          // Facebook
          if (prof.facebook_url) {
            subSections['Social Media'].push(['Facebook', `__CLICKABLE__${prof.facebook_url}`]);
          } else {
            subSections['Social Media'].push(['Facebook', 'Not available for this donor']);
          }
          
          // Instagram
          if (prof.instagram_url) {
            subSections['Social Media'].push(['Instagram', `__CLICKABLE__${prof.instagram_url}`]);
          } else {
            subSections['Social Media'].push(['Instagram', 'Not available for this donor']);
          }
        }
        break;

      case 'News':
        // Google News Articles section
        if (googleNewsData && googleNewsData.success && googleNewsData.articles && googleNewsData.articles.length > 0) {
          subSections['Google News'] = [
            ['__GOOGLE_NEWS_TABLE__', JSON.stringify(googleNewsData.articles)]
          ];
        } else if (googleNewsData && googleNewsData.success && (!googleNewsData.articles || googleNewsData.articles.length === 0)) {
          subSections['Google News'] = [
            ['Status', 'No news articles found for this person']
          ];
        } else {
          subSections['Google News'] = [
            ['Status', googleNewsData ? 'No articles available' : 'Loading Google News...']
          ];
        }
        break;

      case 'Scores':
        // Try to get scores from the data response
        const scoresData = data.scores || {};
        
        // Extract scores
        const capacityScore = scoresData.capacity?.capacity_score || 'Coming Soon';
        const capacityReasoning = scoresData.capacity?.reasoning || '';
        const propensityScore = scoresData.propensity?.propensity_score || 'Coming Soon';
        const propensityReasoning = scoresData.propensity?.reasoning || '';
        const plannedGivingScore = scoresData.planned_giving?.planned_giving_score || 'Coming Soon';
        const plannedGivingReasoning = scoresData.planned_giving?.reasoning || '';
        const overallScore = scoresData.overall?.overall_score || 'Coming Soon';
        const overallReasoning = scoresData.overall?.reasoning || '';
        
        // Build scores section with reasoning paired with each score
        const scoresArray: Array<[string, any]> = [];
        scoresArray.push(['Capacity Score', capacityScore]);
        if (capacityReasoning) scoresArray.push(['Capacity Score Reasoning', capacityReasoning]);
        scoresArray.push(['Propensity Score', propensityScore]);
        if (propensityReasoning) scoresArray.push(['Propensity Score Reasoning', propensityReasoning]);
        scoresArray.push(['Planned Giving Score', plannedGivingScore]);
        if (plannedGivingReasoning) scoresArray.push(['Planned Giving Score Reasoning', plannedGivingReasoning]);
        scoresArray.push(['Overall Score', overallScore]);
        if (overallReasoning) scoresArray.push(['Overall Score Reasoning', overallReasoning]);
        
        subSections['Scores'] = [
          ...scoresArray,
          ['__DISCLAIMER__', 'These are AI-generated scores for informational purposes only and should not be relied upon as definitive indicators of donor capacity or engagement.']
        ];
        break;

      case 'Suggested Ask':
        if (data.suggested_ask) {
          const suggestedAsk = data.suggested_ask;
          const askFields: Array<[string, any]> = [
            ['Low Ask', suggestedAsk.low_ask ? `$${suggestedAsk.low_ask.toLocaleString()}` : 'N/A'],
            ['Mid Ask', suggestedAsk.mid_ask ? `$${suggestedAsk.mid_ask.toLocaleString()}` : 'N/A'],
            ['High Ask', suggestedAsk.high_ask ? `$${suggestedAsk.high_ask.toLocaleString()}` : 'N/A'],
            ['__DISCLAIMER__', 'These are AI-generated recommendations for informational purposes only and should not be relied upon as definitive solicitation guidance.']
          ];
          // Only include ask-related fields from categorized data, exclude other fields
          const suggestedAskFields = fields.filter(([key]) => 
            key.toLowerCase().includes('low ask') ||
            key.toLowerCase().includes('mid ask') ||
            key.toLowerCase().includes('high ask')
          );
          subSections['Suggested Ask'] = [...suggestedAskFields, ...askFields];
        } else {
          subSections['Suggested Ask'] = fields.length > 0 ? fields : [['Suggested Ask Information', 'Coming Soon']];
        }
        break;

      default:
        subSections['Overview'] = fields;
    }
    
    // Add KnowledgeCore AI Analysis as a separate sub-section for categories that support it
    const supportedAiCategories = ['Profile', 'Financial', 'Political Interests', 'Social Media', 'News', 'Affiliations'];
    if (AI_ANALYSIS_ENABLED && supportedAiCategories.includes(category)) {
      subSections['KnowledgeCore AI Analysis'] = [['__AI_INSIGHTS__', category]];
    }
    
    // Remove empty sub-sections
    Object.keys(subSections).forEach(key => {
      if (subSections[key].length === 0) {
        delete subSections[key];
      }
    });
    
    return subSections;
  };



  // Create AI Insights component
  const createAiInsightsSection = (category: string) => {
    const isLoading = aiInsightsLoading[category];
    const result = aiInsightsResults[category];

    return (
      <TableRow key={`ai-insights-${category}`} sx={{ backgroundColor: '#fafafa', border: 'none' }}>
        <TableCell colSpan={2} sx={{ border: 'none', p: 0 }}>
          <Box sx={{ p: 3 }}>
            {/* Show loading indicator while auto-generating (only if no result yet) */}
            {isLoading && !result && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <video autoPlay loop muted style={{ width: '20px', height: '20px' }}>
                  <source src="/rolling_com.mp4" type="video/mp4" />
                </video>
                <Typography variant="body2" color="textSecondary">Auto-generating insights...</Typography>
              </Box>
            )}
            
            {/* Show button only if not loading and no result */}
            {!isLoading && !result && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PsychologyIcon />}
                  onClick={() => handleAiInsights(category)}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: '#6366f1',
                    '&:hover': { backgroundColor: '#5b5bd6' }
                  }}
                >
                  Get AI Insights
                </Button>
              </Box>
            )}
            
            {result && (
              <AIInsightOutput output={result} />
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  // Create the results with tab-based sub-sections
  const createExpandableResults = (category: string, fields: Array<[string, any]>) => {
    // Get sub-sections first (even if fields are empty, we might have predefined sections)
    const subSections = getSubSections(category, fields);
    const hasSubSections = Object.keys(subSections).length > 0;
    const subSectionTitles = Object.keys(subSections);
    
    // Get or initialize selected sub-section for this category
    const selectedTab = selectedSubSection[category] || (subSectionTitles.length > 0 ? subSectionTitles[0] : '');
    const selectedFields = subSections[selectedTab] || [];
    
    if (fields.length === 0 && !hasSubSections) {
      return (
        <Box>
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This category doesn't contain any information for the current search result.
            </Typography>
          </Paper>
        </Box>
      );
    }
    
    return (
      <Box>
        {hasSubSections && (
          <Box sx={{ borderBottom: 3, borderColor: '#e0e0e0', mb: 3, backgroundColor: '#f5f7fa', borderRadius: 1 }}>
            <Tabs
              value={selectedTab}
              onChange={(event, newValue) => setSelectedSubSection(prev => ({ ...prev, [category]: newValue }))}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#6366f1',
                  height: 4
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#555',
                  padding: '12px 16px',
                  minHeight: '52px',
                  backgroundColor: '#d3d3d3',
                  marginRight: '2px',
                  '&.Mui-selected': {
                    color: '#6366f1',
                    backgroundColor: '#fff'
                  },
                  '&:hover': {
                    backgroundColor: '#bfbfbf'
                  }
                }
              }}
            >
              {subSectionTitles.map(title => (
                <Tab key={title} label={title} value={title} />
              ))}
            </Tabs>
          </Box>
        )}
        
        {/* Display Financial Summary before the charts for Financial > Wealth Analysis */}
        {category === 'Financial' && selectedTab === 'Wealth Analysis' && (
          <Box sx={{ mb: 3 }}>
            {financialSummaryLoading ? (
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f0f5ff', borderLeft: '4px solid #1565c0', backgroundColor: '#f0f5ff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <video autoPlay loop muted style={{ width: '24px', height: '24px' }}>
                    <source src="/rolling_com.mp4" type="video/mp4" />
                  </video>
                  <Typography variant="body2" color="text.secondary">
                    Generating financial summary...
                  </Typography>
                </Box>
              </Paper>
            ) : financialSummary ? (
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f0f5ff', borderLeft: '4px solid #1565c0' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
                  Financial Summary
                </Typography>
                <Box
                  component="div"
                  sx={{
                    fontSize: '0.95rem',
                    lineHeight: 1.7,
                    color: '#333',
                    '& ul': {
                      paddingLeft: '20px',
                      marginTop: '8px',
                      marginBottom: '0',
                    },
                    '& li': {
                      marginBottom: '6px',
                      color: '#555',
                    },
                  }}
                >
                  {financialSummary.split('\n').map((line, idx) => {
                    const trimmed = line.trim();
                    if (trimmed.startsWith('•')) {
                      const bulletText = trimmed.substring(1).trim();
                      return (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                          <span style={{ color: '#1565c0', fontWeight: 600, minWidth: '16px' }}>•</span>
                          <span>{bulletText}</span>
                        </Box>
                      );
                    } else if (trimmed && !trimmed.startsWith('-')) {
                      return (
                        <Typography key={idx} variant="body2" sx={{ mb: 1, color: '#555' }}>
                          {trimmed}
                        </Typography>
                      );
                    }
                    return null;
                  })}
                </Box>
              </Paper>
            ) : null}
          </Box>
        )}
        
        {/* Display gauge scores at the top for Financial > Wealth Analysis */}
        {category === 'Financial' && selectedTab === 'Wealth Analysis' && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2, mb: 2 }}>
              {selectedFields.map(([key, value]) => {
                const gaugeConfig = getGaugeConfig(key);
                if (gaugeConfig?.isGauge) {
                  return (
                    <Box key={key}>
                      <ScoreGauge 
                        label={gaugeConfig.label}
                        value={value}
                        maxValue={99999}
                        valueDirection={gaugeConfig.valueDirection}
                      />
                    </Box>
                  );
                }
                return null;
              })}
            </Box>
          </Box>
        )}
        
        {/* Display Biographical Summary before the field table */}
        {category === 'Profile' && selectedTab === 'Biographical' && (
          <Box sx={{ mb: 3 }}>
            {biologicalSummaryLoading ? (
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f5f9ff', borderLeft: '4px solid #1976d2', backgroundColor: '#f5f9ff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <video autoPlay loop muted style={{ width: '24px', height: '24px' }}>
                    <source src="/rolling_com.mp4" type="video/mp4" />
                  </video>
                  <Typography variant="body2" color="text.secondary">
                    Generating profile summary...
                  </Typography>
                </Box>
              </Paper>
            ) : biologicalSummary ? (
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f5f9ff', borderLeft: '4px solid #1976d2' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Profile
                </Typography>
                <Box
                  component="div"
                  sx={{
                    fontSize: '0.95rem',
                    lineHeight: 1.7,
                    color: '#333',
                    '& ul': {
                      paddingLeft: '20px',
                      marginTop: '8px',
                      marginBottom: '0',
                    },
                    '& li': {
                      marginBottom: '6px',
                      color: '#555',
                    },
                  }}
                >
                  <AIInsightMarkdown output={biologicalSummary} />
                </Box>
              </Paper>
            ) : null}
          </Box>
        )}
        
        {/* Display Consumer Behavior Summary before the field table */}
        {category === 'Consumer Behavior' && (
          <Box sx={{ mb: 3 }}>
            {consumerBehaviorSummaryLoading ? (
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f0f8e8', borderLeft: '4px solid #388e3c', backgroundColor: '#f0f8e8' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <video autoPlay loop muted style={{ width: '24px', height: '24px' }}>
                    <source src="/rolling_com.mp4" type="video/mp4" />
                  </video>
                  <Typography variant="body2" color="text.secondary">
                    Generating consumer behavior summary...
                  </Typography>
                </Box>
              </Paper>
            ) : consumerBehaviorSummary ? (
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f0f8e8', borderLeft: '4px solid #388e3c' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#388e3c' }}>
                  Consumer Behavior Summary
                </Typography>
                <Box
                  component="div"
                  sx={{
                    fontSize: '0.95rem',
                    lineHeight: 1.7,
                    color: '#333',
                    '& ul': {
                      paddingLeft: '20px',
                      marginTop: '8px',
                      marginBottom: '0',
                    },
                    '& li': {
                      marginBottom: '6px',
                      color: '#555',
                    },
                  }}
                >
                  {consumerBehaviorSummary.split('\n').map((line, idx) => {
                    const trimmed = line.trim();
                    if (trimmed.startsWith('•')) {
                      const bulletText = trimmed.substring(1).trim();
                      return (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                          <span style={{ color: '#388e3c', fontWeight: 600, minWidth: '16px' }}>•</span>
                          <span>{bulletText}</span>
                        </Box>
                      );
                    } else if (trimmed && !trimmed.startsWith('-')) {
                      return (
                        <Typography key={idx} variant="body2" sx={{ mb: 1, color: '#555' }}>
                          {trimmed}
                        </Typography>
                      );
                    }
                    return null;
                  })}
                </Box>
              </Paper>
            ) : null}
          </Box>
        )}
        
        {hasSubSections && selectedFields.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {/* Collapsible Field Table Header */}
            <Box
              onClick={() => {
                const key = `${category}-${selectedTab}`;
                setFieldTableCollapsed(prev => ({
                  ...prev,
                  [key]: !prev[key]
                }));
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                px: 2,
                bgcolor: '#2c3e50',
                color: '#fff',
                cursor: 'pointer',
                userSelect: 'none',
                borderRadius: '4px 4px 0 0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#34495e',
                  transform: 'translateY(-2px) scaleX(1.02)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                }
              }}
            >
              <span style={{ 
                fontSize: '1.2rem', 
                transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {(fieldTableCollapsed[`${category}-${selectedTab}`] ?? false) ? '▶' : '▼'}
              </span>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                Field Details
              </Typography>
            </Box>
            
            {/* Collapsible Field Table Content */}
            {!(fieldTableCollapsed[`${category}-${selectedTab}`] ?? false) && (
              <Box sx={{ borderTop: 'none' }}>
                {createResultsTable(selectedFields, category, selectedTab)}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Create the results table component
  const createResultsTable = (fields: Array<[string, any]>, category?: string, selectedTab?: string) => {
    if (fields.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This category doesn't contain any information for the current search result.
          </Typography>
        </Paper>
      );
    }

    // Skip header for KnowledgeCore AI Analysis tab and Google News tab
    const isAiTab = selectedTab === 'KnowledgeCore AI Analysis';
    const isGoogleNewsTab = selectedTab === 'Google News';

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table sx={{ minWidth: 650 }} aria-label="categorized results table">
          {!isAiTab && !isGoogleNewsTab && (
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: '#283E56',
                '& .MuiTableCell-head': {
                  color: 'white',
                  fontWeight: 'bold',
                }
              }}>
                <TableCell sx={{ width: '35%', fontSize: '0.875rem' }}>
                  Field Name
                </TableCell>
                <TableCell sx={{ width: '65%', fontSize: '0.875rem' }}>
                  {category === 'Scores' ? 'Range (1-10)' : 'Value'}
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {fields.map(([key, value], index) => {
              // Skip gauge fields if displayed at the top (Financial > Wealth Analysis)
              if (category === 'Financial' && selectedTab === 'Wealth Analysis' && getGaugeConfig(key)?.isGauge) {
                return null;
              }

              // Special handling for AI insights
              if (key === '__AI_INSIGHTS__') {
                return createAiInsightsSection(value);
              }

              // Special handling for disclaimer
              if (key === '__DISCLAIMER__') {
                return (
                  <TableRow key={`disclaimer-${index}`}>
                    <TableCell colSpan={2} sx={{ 
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #bbdefb',
                      borderRadius: 1,
                      padding: 2
                    }}>
                      <Typography variant="caption" sx={{ 
                        color: '#1565c0',
                        fontStyle: 'italic',
                        display: 'block'
                      }}>
                        {value}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }

              // Special handling for clickable LinkedIn URLs
              if (typeof value === 'string' && value.startsWith('__LINKEDIN_CLICKABLE__')) {
                const url = value.replace('__LINKEDIN_CLICKABLE__', '');
                return (
                  <TableRow key={`linkedin-url-${index}`} sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid', borderColor: 'divider' }}>
                      {key}
                    </TableCell>
                    <TableCell>
                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#0a66c2', textDecoration: 'none', fontWeight: 500 }}>
                        {url}
                      </a>
                    </TableCell>
                  </TableRow>
                );
              }
              
              // Special handling for clickable URLs
              if (typeof value === 'string' && value.startsWith('__CLICKABLE__')) {
                const url = value.replace('__CLICKABLE__', '');
                return (
                  <TableRow key={`clickable-url-${index}`} sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid', borderColor: 'divider' }}>
                      {key}
                    </TableCell>
                    <TableCell>
                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 500 }}>
                        {url}
                      </a>
                    </TableCell>
                  </TableRow>
                );
              }
              
              // Special handling for phone validation table
              if (key === '__PHONE_TABLE__') {
                const phoneData = JSON.parse(value as string);
                return (
                  <React.Fragment key={`phone-table-${index}`}>
                    {/* Phone table headers */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                width: '60px',
                                textAlign: 'center'
                              }}>S/N</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Phone Type</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Phone Number</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>DNC Status</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1
                              }}>DNC Revised Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {phoneData.map((phone: any, phoneIndex: number) => (
                              <TableRow 
                                key={phoneIndex}
                                sx={{
                                  '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                                  '&:hover': { backgroundColor: '#e3f2fd' }
                                }}
                              >
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  textAlign: 'center',
                                  fontWeight: 600,
                                  color: '#666'
                                }}>{phoneIndex + 1}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{phone.type}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1, 
                                  fontWeight: 600,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{phone.number}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  color: phone.dnc_status === 'Yes' ? '#d32f2f' : '#2e7d32',
                                  fontWeight: 600,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{phone.dnc_status}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1 
                                }}>{phone.dnc_date}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }
              
              // Special handling for validation buttons
              if (key === '__PHONE_VALIDATION_BUTTON__') {
                return (
                  <TableRow key={`phone-validation-${index}`}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid', borderColor: 'divider' }}>
                      Action Required
                    </TableCell>
                    <TableCell>
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handlePhoneValidation}
                          disabled={phoneValidationLoading || !searchCriteria}
                        >
                          {phoneValidationLoading ? 'Validating...' : 'Validate Phone Numbers'}
                        </Button>
                        {!searchCriteria && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                            Search criteria not available
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }

              // Custom email table rendering
              if (key === '__EMAIL_TABLE__') {
                const emails = JSON.parse(value);
                return (
                  <React.Fragment key={`email-table-${index}`}>
                    <TableRow>
                      <TableCell 
                        colSpan={2} 
                        sx={{ 
                          p: 0,
                          borderBottom: 'none'
                        }}
                      >
                        <Table size="small" sx={{ backgroundColor: '#f8f9fa' }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#e9ecef' }}>
                              <TableCell sx={{ 
                                fontSize: '0.875rem', 
                                fontWeight: 700, 
                                py: 1.5,
                                borderRight: '1px solid #e0e0e0',
                                textAlign: 'center',
                                width: '60px'
                              }}>S/N</TableCell>
                              <TableCell sx={{ 
                                fontSize: '0.875rem', 
                                fontWeight: 700, 
                                py: 1.5,
                                borderRight: '1px solid #e0e0e0',
                                width: '120px'
                              }}>Email Type</TableCell>
                              <TableCell sx={{ 
                                fontSize: '0.875rem', 
                                fontWeight: 700, 
                                py: 1.5 
                              }}>Email Address</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {emails.map((email: any, emailIndex: number) => (
                              <TableRow 
                                key={`email-${emailIndex}`}
                                sx={{
                                  '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                                  '&:hover': { backgroundColor: '#e3f2fd' }
                                }}
                              >
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  textAlign: 'center',
                                  fontWeight: 600,
                                  color: '#666'
                                }}>{emailIndex + 1}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{email.type}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1, 
                                  fontWeight: 600
                                }}>{email.address}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }
              
              if (key === '__EMAIL_VALIDATION_BUTTON__') {
                return (
                  <TableRow key={`email-validation-${index}`}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid', borderColor: 'divider' }}>
                      Action Required
                    </TableCell>
                    <TableCell>
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleEmailValidation}
                          disabled={emailValidationLoading || !searchCriteria}
                          sx={{ ml: 1 }}
                        >
                          {emailValidationLoading ? 'Validating...' : 'Validate Email Address'}
                        </Button>
                        {!searchCriteria && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                            Search criteria not available
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }

              // Contributions table rendering
              if (key === '__CONTRIBUTIONS_TABLE__') {
                const contributions = JSON.parse(value as string);
                const formatUrl = (url: string) => {
                  if (!url) return '';
                  if (!url.startsWith('http://') && !url.startsWith('https://')) {
                    return `https://${url}`;
                  }
                  return url;
                };
                return (
                  <React.Fragment key={`contributions-table-${index}`}>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                width: '60px',
                                textAlign: 'center'
                              }}>S/N</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Name/Organization</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>URL</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Donation Amount</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Donation Date</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1
                              }}>Verification Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contributions.map((contribution: any, contIndex: number) => {
                              const fullUrl = formatUrl(contribution.url);
                              return (
                              <TableRow 
                                key={contIndex}
                                sx={{
                                  '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                                  '&:hover': { backgroundColor: '#e3f2fd' }
                                }}
                              >
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  textAlign: 'center',
                                  fontWeight: 600,
                                  color: '#666'
                                }}>{contIndex + 1}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{contribution.recipient || 'N/A'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.75rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {fullUrl ? (
                                    <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                                      {fullUrl}
                                    </a>
                                  ) : 'N/A'}
                                </TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1, 
                                  fontWeight: 600,
                                  borderRight: '1px solid #e0e0e0',
                                  color: '#2e7d32'
                                }}>{contribution.donation_amount && contribution.donation_amount !== '' ? `$${contribution.donation_amount}` : 'N/A'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{contribution.donation_date || 'N/A'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1
                                }}>
                                  <Chip 
                                    label={contribution.verification_status || 'Unknown'}
                                    size="small"
                                    color={contribution.verification_status === 'Verified' ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                            })}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }

              // FEC Contributions table rendering
              if (key === '__FEC_CONTRIBUTIONS_TABLE__') {
                const fecContributions = JSON.parse(value as string);
                const formatCurrency = (amount: number | null) => {
                  if (!amount) return 'N/A';
                  return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(amount);
                };

                const formatDate = (dateStr: string | null) => {
                  if (!dateStr) return 'N/A';
                  try {
                    const date = new Date(dateStr);
                    return date.toLocaleDateString('en-US');
                  } catch {
                    return dateStr;
                  }
                };

                return (
                  <React.Fragment key={`fec-contributions-table-${index}`}>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                width: '50px',
                                textAlign: 'center',
                                backgroundColor: '#e8eaf6'
                              }}>S/N</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e8eaf6'
                              }}>Contributor Name</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e8eaf6'
                              }}>Amount</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e8eaf6'
                              }}>Date</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e8eaf6'
                              }}>Committee Name</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e8eaf6'
                              }}>Committee Type</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e8eaf6'
                              }}>Election Type</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e8eaf6'
                              }}>Location</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                backgroundColor: '#e8eaf6'
                              }}>Employer/Occupation</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {fecContributions.sort((a: any, b: any) => {
                              const dateA = a.contribution_receipt_date ? new Date(a.contribution_receipt_date).getTime() : 0;
                              const dateB = b.contribution_receipt_date ? new Date(b.contribution_receipt_date).getTime() : 0;
                              return dateB - dateA; // Descending order (newest first)
                            }).slice(fecPaginationPage * fecPaginationRowsPerPage, fecPaginationPage * fecPaginationRowsPerPage + fecPaginationRowsPerPage).map((contribution: any, contIndex: number) => {
                              const fullName = [
                                contribution.contributor_first_name,
                                contribution.contributor_middle_name,
                                contribution.contributor_last_name
                              ].filter(Boolean).join(' ') || 'N/A';

                              const location = [
                                contribution.contributor_city,
                                contribution.contributor_state,
                                contribution.contributor_zip ? contribution.contributor_zip.substring(0, 5) : null
                              ].filter(Boolean).join(', ') || 'N/A';

                              const employerOccupation = [
                                contribution.contributor_employer,
                                contribution.contributor_occupation
                              ].filter(Boolean).join(' | ') || 'N/A';

                              return (
                                <TableRow 
                                  key={contIndex}
                                  sx={{
                                    '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                                    '&:hover': { backgroundColor: '#e3f2fd' }
                                  }}
                                >
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1,
                                    borderRight: '1px solid #e0e0e0',
                                    textAlign: 'center',
                                    fontWeight: 600,
                                    color: '#666'
                                  }}>{contIndex + 1}</TableCell>
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1,
                                    borderRight: '1px solid #e0e0e0'
                                  }}>{fullName}</TableCell>
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1, 
                                    fontWeight: 600,
                                    borderRight: '1px solid #e0e0e0',
                                    color: '#2e7d32',
                                    textAlign: 'right'
                                  }}>{formatCurrency(contribution.contribution_receipt_amount)}</TableCell>
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1,
                                    borderRight: '1px solid #e0e0e0'
                                  }}>{formatDate(contribution.contribution_receipt_date)}</TableCell>
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1,
                                    borderRight: '1px solid #e0e0e0',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    <span title={contribution.committee_name || 'N/A'}>
                                      {contribution.committee_name || 'N/A'}
                                    </span>
                                  </TableCell>
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1,
                                    borderRight: '1px solid #e0e0e0'
                                  }}>
                                    {contribution.committee_type_full || 'N/A'}
                                  </TableCell>
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1,
                                    borderRight: '1px solid #e0e0e0'
                                  }}>
                                    {contribution.fec_election_type_desc || 'N/A'}
                                  </TableCell>
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1,
                                    borderRight: '1px solid #e0e0e0'
                                  }}>
                                    {location}
                                  </TableCell>
                                  <TableCell sx={{ 
                                    fontSize: '0.875rem', 
                                    py: 1
                                  }}>
                                    {employerOccupation}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          component="div"
                          count={fecContributions.length}
                          rowsPerPage={fecPaginationRowsPerPage}
                          page={fecPaginationPage}
                          onPageChange={(event, newPage) => setFecPaginationPage(newPage)}
                          onRowsPerPageChange={(event) => {
                            setFecPaginationRowsPerPage(parseInt(event.target.value, 10));
                            setFecPaginationPage(0);
                          }}
                          sx={{
                            backgroundColor: '#f9f9f9',
                            borderTop: '1px solid #e0e0e0',
                            '& .MuiTablePagination-select': {
                              marginRight: '8px',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }

              // IRS Form 990 nonprofit records table rendering
              if (key === '__IRS_FORM990_TABLE__') {
                const irsRecords = JSON.parse(value as string);
                const formatCurrency = (amount: number | null) => {
                  if (!amount) return 'N/A';
                  return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(amount);
                };

                return (
                  <React.Fragment key={`irs-form990-table-${index}`}>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                width: '50px',
                                textAlign: 'center',
                                backgroundColor: '#e3f2fd'
                              }}>S/N</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e3f2fd'
                              }}>Officer Name</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e3f2fd'
                              }}>Officer Title</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e3f2fd'
                              }}>Organization Name</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: '#e3f2fd'
                              }}>Gross Receipts</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                backgroundColor: '#e3f2fd'
                              }}>Tax Year</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {irsRecords.slice(irsPaginationPage * irsPaginationRowsPerPage, irsPaginationPage * irsPaginationRowsPerPage + irsPaginationRowsPerPage).map((record: any, recordIndex: number) => (
                              <TableRow 
                                key={recordIndex}
                                sx={{
                                  '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                                  '&:hover': { backgroundColor: '#e3f2fd' }
                                }}
                              >
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  textAlign: 'center',
                                  fontWeight: 600,
                                  color: '#666'
                                }}>{recordIndex + 1}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{record.officer_name || 'N/A'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{record.officer_title || 'N/A'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  maxWidth: '200px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  <span title={record.organization_name || 'N/A'}>
                                    {record.organization_name || 'N/A'}
                                  </span>
                                </TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  fontWeight: 600,
                                  color: '#2e7d32',
                                  textAlign: 'right'
                                }}>{formatCurrency(record.gross_receipts_amount)}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  textAlign: 'center'
                                }}>{record.tax_year || 'N/A'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          component="div"
                          count={irsRecords.length}
                          rowsPerPage={irsPaginationRowsPerPage}
                          page={irsPaginationPage}
                          onPageChange={(event, newPage) => setIrsPaginationPage(newPage)}
                          onRowsPerPageChange={(event) => {
                            setIrsPaginationRowsPerPage(parseInt(event.target.value, 10));
                            setIrsPaginationPage(0);
                          }}
                          sx={{
                            backgroundColor: '#f9f9f9',
                            borderTop: '1px solid #e0e0e0',
                            '& .MuiTablePagination-select': {
                              marginRight: '8px',
                            }
                          }}
                        />
                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                          <TableCell colSpan={6} sx={{ 
                            border: '1px solid #90caf9',
                            padding: 2
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: '#1565c0',
                              fontStyle: 'italic',
                              display: 'block'
                            }}>
                              Disclaimer: Search results may return multiple individuals with identical or similar names. Users are responsible for verifying the accuracy of the returned data and confirming the identity of the intended subject before using this information for any purpose.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }

              // Apollo Business & Professional table rendering with sub-tabs
              if (key === '__APOLLO_TABLE__') {
                const apolloRows = JSON.parse(value as string);
                
                return (
                  <React.Fragment key={`apollo-table-${index}`}>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#fff' }}>
                          <Tabs value={apolloSubTab} onChange={(e, newValue) => setApolloSubTab(newValue)}>
                            <Tab label="Personal" value="personal" />
                            <Tab label="Organization" value="organization" />
                          </Tabs>
                        </Box>
                        
                        {apolloSubTab === 'personal' && (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ 
                                  fontWeight: 700, 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  width: '50%',
                                  backgroundColor: '#e3f2fd',
                                  color: '#424242'
                                }}>Field</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 700, 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  backgroundColor: '#e3f2fd',
                                  color: '#424242'
                                }}>Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {apolloRows
                                .filter((row: any) => {
                                  const fieldName = row[0].toLowerCase();
                                  return !fieldName.startsWith('organization -') &&
                                         !fieldName.includes('employment') &&
                                         (fieldName.includes('photo') ||
                                         fieldName.includes('first_name') ||
                                         fieldName.includes('last_name') ||
                                         fieldName.includes('email') ||
                                         fieldName.includes('phone') ||
                                         (fieldName === 'name') ||
                                         (fieldName === 'headline') ||
                                         fieldName.includes('title') ||
                                         fieldName.includes('street') ||
                                         fieldName.includes('city') ||
                                         fieldName.includes('state'));
                                })
                                .slice(apolloPaginationPage * apolloPaginationRowsPerPage, apolloPaginationPage * apolloPaginationRowsPerPage + apolloPaginationRowsPerPage)
                                .map((row: any, rowIndex: number) => {
                                const [fieldName, fieldValue] = row;
                                let displayValue: any = fieldValue;
                                
                                if (fieldName.toLowerCase().includes('photo') && typeof fieldValue === 'string' && fieldValue) {
                                  let photoUrl = fieldValue;
                                  if (fieldValue.startsWith('__CLICKABLE__')) {
                                    photoUrl = fieldValue.replace('__CLICKABLE__', '');
                                  }
                                  displayValue = (
                                    <img 
                                      src={photoUrl} 
                                      alt="Profile"
                                      style={{ maxWidth: '150px', maxHeight: '150px', borderRadius: '4px', border: '1px solid #e0e0e0', objectFit: 'cover' }}
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  );
                                }
                                
                                return (
                                  <TableRow key={rowIndex} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' }, '&:hover': { backgroundColor: '#e3f2fd' } }}>
                                    <TableCell sx={{ fontSize: '0.875rem', py: 1, borderRight: '1px solid #e0e0e0', fontWeight: 600, color: '#424242', width: '50%', wordBreak: 'break-word' }}>
                                      {fieldName.toLowerCase().includes('photo') ? 'Photo' : fieldName}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem', py: 1, wordBreak: 'break-word', maxWidth: '500px', whiteSpace: 'pre-wrap' }}>
                                      {displayValue}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        )}
                        
                        {apolloSubTab === 'organization' && (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ 
                                  fontWeight: 700, 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  width: '50%',
                                  backgroundColor: '#e3f2fd',
                                  color: '#424242'
                                }}>Field</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 700, 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  backgroundColor: '#e3f2fd',
                                  color: '#424242'
                                }}>Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(() => {
                                const employmentRow = apolloRows.find((row: any) => row[0].toLowerCase().includes('employment'));
                                const organizationFields = apolloRows.filter((row: any) => row[0].toLowerCase().startsWith('organization -'));
                                const allOrgRows = employmentRow ? [employmentRow] : [];
                                
                                return allOrgRows
                                  .slice(apolloPaginationPage * apolloPaginationRowsPerPage, apolloPaginationPage * apolloPaginationRowsPerPage + apolloPaginationRowsPerPage)
                                  .map((row: any, rowIndex: number) => {
                                  const [fieldName, fieldValue] = row;
                                  const globalRowIndex = apolloPaginationPage * apolloPaginationRowsPerPage + rowIndex;
                                  const isEmploymentHistory = fieldName.toLowerCase().includes('employment') && typeof fieldValue === 'string' && fieldValue.startsWith('[');
                                  const isExpanded = !expandedEmploymentIndices.has(globalRowIndex);
                                  
                                  if (isEmploymentHistory) {
                                    try {
                                      const employmentEntries = JSON.parse(fieldValue);
                                      const organizationFieldsMap: any = {};
                                      organizationFields.forEach((orgRow: any) => {
                                        organizationFieldsMap[orgRow[0]] = orgRow[1];
                                      });
                                      
                                      return (
                                        <React.Fragment key={rowIndex}>
                                          <TableRow 
                                            sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' }, '&:hover': { backgroundColor: '#e3f2fd' }, cursor: 'pointer' }}
                                            onClick={() => {
                                              const newSet = new Set(expandedEmploymentIndices);
                                              if (newSet.has(globalRowIndex)) {
                                                newSet.delete(globalRowIndex);
                                              } else {
                                                newSet.add(globalRowIndex);
                                              }
                                              setExpandedEmploymentIndices(newSet);
                                            }}
                                          >
                                            <TableCell sx={{ fontSize: '0.875rem', py: 1, borderRight: '1px solid #e0e0e0', fontWeight: 600, color: '#424242', width: '50%' }}>
                                              {fieldName}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.875rem', py: 1 }}>
                                              <span style={{ fontWeight: 600, color: '#1565c0', cursor: 'pointer' }}>
                                                {employmentEntries.length} {employmentEntries.length === 1 ? 'position' : 'positions'}
                                              </span>
                                              {!isExpanded && <span> (Click to expand)</span>}
                                            </TableCell>
                                          </TableRow>
                                          
                                          {isExpanded && employmentEntries.map((entry: any, entryIndex: number) => (
                                            <React.Fragment key={`emp-${entryIndex}`}>
                                              <TableRow sx={{ backgroundColor: '#f9f9f9', borderLeft: '4px solid #90caf9' }}>
                                                <TableCell colSpan={2} sx={{ py: 2, pl: 3 }}>
                                                  <div style={{ marginBottom: '12px' }}>
                                                    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1565c0', marginBottom: '6px' }}>
                                                      {entry.title || 'N/A'} {entry.organization_name ? `@ ${entry.organization_name}` : ''}
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                                                      {entry.start_date ? new Date(entry.start_date).toLocaleDateString() : 'Start date unknown'} 
                                                      {' — '}
                                                      {entry.current ? 'Present' : entry.end_date ? new Date(entry.end_date).toLocaleDateString() : 'End date unknown'}
                                                    </div>
                                                    {entry.description && (
                                                      <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px', fontStyle: 'italic' }}>
                                                        {entry.description}
                                                      </div>
                                                    )}
                                                  </div>
                                                </TableCell>
                                              </TableRow>
                                              
                                              {entryIndex === 0 && Object.keys(organizationFieldsMap).length > 0 && (
                                                <TableRow sx={{ backgroundColor: '#f5f5f5', borderLeft: '4px solid #90caf9' }}>
                                                  <TableCell colSpan={2} sx={{ py: 2, pl: 3 }}>
                                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#424242', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e0e0e0' }}>
                                                      Company Details
                                                    </div>
                                                    {Object.entries(organizationFieldsMap).map(([orgFieldName, orgFieldValue]: [string, any]) => (
                                                      <div key={orgFieldName} style={{ marginBottom: '10px' }}>
                                                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#424242', marginBottom: '3px' }}>
                                                          {orgFieldName.replace('Organization - ', '').replace(/([A-Z])/g, ' $1').trim()}
                                                        </div>
                                                        <div style={{ fontSize: '0.8rem', color: '#555', marginLeft: '8px', wordBreak: 'break-word' }}>
                                                          {typeof orgFieldValue === 'string' && orgFieldValue.startsWith('__LINKEDIN_CLICKABLE__') ? (
                                                            <a href={orgFieldValue.replace('__LINKEDIN_CLICKABLE__', '')} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none' }}>
                                                              {orgFieldValue.replace('__LINKEDIN_CLICKABLE__', '')}
                                                            </a>
                                                          ) : typeof orgFieldValue === 'string' && orgFieldValue.startsWith('__CLICKABLE__') ? (
                                                            <a href={orgFieldValue.replace('__CLICKABLE__', '')} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none' }}>
                                                              {orgFieldValue.replace('__CLICKABLE__', '')}
                                                            </a>
                                                          ) : typeof orgFieldValue === 'string' && (orgFieldValue.startsWith('http://') || orgFieldValue.startsWith('https://')) ? (
                                                            <a href={orgFieldValue} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none' }}>
                                                              {orgFieldValue}
                                                            </a>
                                                          ) : (
                                                            orgFieldValue
                                                          )}
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </TableCell>
                                                </TableRow>
                                              )}
                                            </React.Fragment>
                                          ))}
                                        </React.Fragment>
                                      );
                                    } catch (e) {
                                      return null;
                                    }
                                  }
                                  
                                  return null;
                                });
                              })()}
                            </TableBody>
                          </Table>
                        )}
                        
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          component="div"
                          count={apolloSubTab === 'personal'
                            ? apolloRows.filter((row: any) => !row[0].toLowerCase().startsWith('organization -') && !row[0].toLowerCase().includes('employment') && (row[0].toLowerCase().includes('photo') || row[0].toLowerCase().includes('first_name') || row[0].toLowerCase().includes('last_name') || row[0].toLowerCase().includes('email') || row[0].toLowerCase().includes('phone') || row[0].toLowerCase() === 'name' || row[0].toLowerCase() === 'headline' || row[0].toLowerCase().includes('title') || row[0].toLowerCase().includes('street') || row[0].toLowerCase().includes('city') || row[0].toLowerCase().includes('state'))).length
                            : apolloRows.filter((row: any) => row[0].toLowerCase().includes('employment')).length
                          }
                          rowsPerPage={apolloPaginationRowsPerPage}
                          page={apolloPaginationPage}
                          onPageChange={(event, newPage) => setApolloPaginationPage(newPage)}
                          onRowsPerPageChange={(event) => {
                            setApolloPaginationRowsPerPage(parseInt(event.target.value, 10));
                            setApolloPaginationPage(0);
                          }}
                          sx={{
                            backgroundColor: '#f9f9f9',
                            borderTop: '1px solid #e0e0e0',
                            '& .MuiTablePagination-select': {
                              marginRight: '8px',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }

              // Transaction table rendering
              if (key === '__TRANSACTION_TABLE__') {
                const transactions = JSON.parse(value as string);
                return (
                  <React.Fragment key={`transaction-table-${index}`}>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0',
                                width: '60px',
                                textAlign: 'center'
                              }}>S/N</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Gift Date</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Gift Amount</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Gift Type</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1,
                                borderRight: '1px solid #e0e0e0'
                              }}>Campaign ID</TableCell>
                              <TableCell sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.875rem', 
                                py: 1
                              }}>Fund Description</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {transactions.map((transaction: any, txIndex: number) => (
                              <TableRow 
                                key={txIndex}
                                sx={{
                                  '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                                  '&:hover': { backgroundColor: '#e3f2fd' }
                                }}
                              >
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0',
                                  textAlign: 'center',
                                  fontWeight: 600,
                                  color: '#666'
                                }}>{txIndex + 1}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{transaction.gift_date || 'N/A'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1, 
                                  fontWeight: 600,
                                  borderRight: '1px solid #e0e0e0',
                                  color: '#2e7d32'
                                }}>${transaction.gift_amount?.toFixed(2) || '0.00'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{transaction.gift_type || 'Unknown'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1,
                                  borderRight: '1px solid #e0e0e0'
                                }}>{transaction.campaign_id || 'N/A'}</TableCell>
                                <TableCell sx={{ 
                                  fontSize: '0.875rem', 
                                  py: 1
                                }}>{transaction.fund_description || 'N/A'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }

              // Google News articles table rendering
              if (key === '__GOOGLE_NEWS_TABLE__') {
                const newsArticles = JSON.parse(value as string);
                const resolveSourceName = (src: any): string => {
                  if (!src) return 'N/A';
                  if (typeof src === 'string') return src;
                  if (typeof src === 'object') return src.name || 'N/A';
                  return String(src);
                };
                const resolveSourceIcon = (src: any): string => {
                  if (!src || typeof src !== 'object') return '';
                  return src.icon || '';
                };
                const resolveAuthors = (src: any): string => {
                  if (!src || typeof src !== 'object') return '';
                  const authors = src.authors;
                  if (!Array.isArray(authors) || authors.length === 0) return '';
                  return authors.join(', ');
                };
                const fmtIsoDate = (iso: string): string => {
                  if (!iso) return '';
                  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
                  catch { return ''; }
                };
                const paginated = newsArticles.slice(
                  googleNewsPaginationPage * googleNewsPaginationRowsPerPage,
                  googleNewsPaginationPage * googleNewsPaginationRowsPerPage + googleNewsPaginationRowsPerPage
                );
                return (
                  <React.Fragment key={`google-news-cards-${index}`}>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ p: 2, border: 'none', backgroundColor: '#f9f9f9' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {paginated.map((article: any, artIndex: number) => {
                            const sourceName = resolveSourceName(article.source);
                            const sourceIcon = resolveSourceIcon(article.source);
                            const authors    = resolveAuthors(article.source);
                            const niceDate   = fmtIsoDate(article.iso_date) || (article.date ? article.date.split(',')[0].trim() : 'N/A');
                            const thumb      = article.thumbnail || article.thumbnail_small || '';
                            return (
                              <Paper
                                key={artIndex}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  borderLeft: '4px solid #455a64',
                                  backgroundColor: '#fff',
                                  display: 'flex',
                                  gap: 2,
                                  '&:hover': { backgroundColor: '#f5f5f5' }
                                }}
                              >
                                {/* Thumbnail */}
                                {thumb && (
                                  <Box sx={{ flexShrink: 0 }}>
                                    <img
                                      src={thumb}
                                      alt=""
                                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, display: 'block' }}
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  </Box>
                                )}

                                {/* Content */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  {/* Title */}
                                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75, lineHeight: 1.4 }}>
                                    {article.link ? (
                                      <a href={article.link} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none' }}>
                                        {article.title || 'N/A'}
                                      </a>
                                    ) : (article.title || 'N/A')}
                                  </Typography>

                                  {/* Source row: icon + name + authors */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                                    {sourceIcon && (
                                      <img src={sourceIcon} alt="" style={{ width: 14, height: 14, borderRadius: 2 }}
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    )}
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>{sourceName}</Typography>
                                    {authors && (
                                      <Typography variant="caption" sx={{ color: '#6B7280' }}>· By {authors}</Typography>
                                    )}
                                  </Box>

                                  {/* Snippet */}
                                  {article.snippet && article.snippet !== 'N/A' && (
                                    <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '0.82rem', lineHeight: 1.5 }}>
                                      {article.snippet}
                                    </Typography>
                                  )}

                                  {/* Date — below snippet */}
                                  <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.75, display: 'block' }}>{niceDate}</Typography>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Box>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          component="div"
                          count={newsArticles.length}
                          rowsPerPage={googleNewsPaginationRowsPerPage}
                          page={googleNewsPaginationPage}
                          onPageChange={(event, newPage) => setGoogleNewsPaginationPage(newPage)}
                          onRowsPerPageChange={(event) => {
                            setGoogleNewsPaginationRowsPerPage(parseInt(event.target.value, 10));
                            setGoogleNewsPaginationPage(0);
                          }}
                          sx={{ backgroundColor: '#f9f9f9', borderTop: '1px solid #e0e0e0', mt: 1 }}
                        />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }

              // Fetch transactions button
              if (key === '__FETCH_TRANSACTIONS_BUTTON__') {
                return (
                  <TableRow key={`fetch-transactions-${index}`}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', borderRight: '1px solid', borderColor: 'divider' }}>
                      Transaction History
                    </TableCell>
                    <TableCell>
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleFetchTransactions}
                          disabled={transactionLoading}
                        >
                          {transactionLoading ? 'Loading...' : 'Load Giving History'}
                        </Button>
                        {transactionData?.error && (
                          <Typography variant="caption" display="block" color="error" sx={{ mt: 1 }}>
                            {transactionData.error}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }
              
              // Check if this field should be rendered as a gauge
              const gaugeConfig = getGaugeConfig(key);
              if (gaugeConfig?.isGauge) {
                return (
                  <TableRow
                    key={`${key}-${index}`}
                    sx={{
                      backgroundColor: '#fafafa',
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                  >
                    <TableCell colSpan={2} sx={{ p: 2, border: 'none' }}>
                      <ScoreGauge 
                        label={gaugeConfig.label}
                        value={value}
                        maxValue={99999}
                        valueDirection={gaugeConfig.valueDirection}
                      />
                    </TableCell>
                  </TableRow>
                );
              }
              
              // Regular table row rendering
              return (
                <TableRow
                  key={`${key}-${index}`}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'grey.50',
                    },
                    '&:hover': {
                      backgroundColor: '#6B7280',
                      transform: 'scale(1.005)',
                      transition: 'all 0.2s ease-in-out',
                      '& .MuiTableCell-root': {
                        color: 'white',
                      }
                    },
                    cursor: 'default',
                  }}
                >
                  <TableCell 
                    component="th" 
                    scope="row" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      verticalAlign: 'top',
                      py: 2,
                    }}
                  >
                    {formatFieldName(key)}
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: '0.875rem',
                    py: 2,
                    verticalAlign: 'top',
                  }}>
                    {formatValue(value)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Box sx={{ 
          p: 2, 
          backgroundColor: '#283E56', 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
            {fields.length} {fields.length === 1 ? 'field' : 'fields'} with data
          </Typography>
          <Chip 
            label={`${((fields.length / Object.values(allCategorizedData).flat().length) * 100).toFixed(1)}% of total`}
            size="small"
            sx={{ 
              bgcolor: 'white',
              color: '#283E56',
              fontWeight: 600,
            }}
          />
        </Box>
      </TableContainer>
    );
  };

  const tabLabels = [
    'Profile',
    'Consumer Behavior',
    'Financial',
    'Political Interests', 
    'Contact Validation',
    'Philanthropy',
    'Affiliations',
    'Social Media',
    'News',
    'Scores',
    'Suggested Ask'
  ];

  // Define color themes for each tab
  const tabColors = {
    'Profile': { primary: '#1976d2', light: '#e3f2fd', chip: '#1565c0' },
    'Consumer Behavior': { primary: '#388e3c', light: '#e8f5e8', chip: '#2e7d32' },
    'Financial': { primary: '#f57c00', light: '#fff3e0', chip: '#ef6c00' },
    'Political Interests': { primary: '#7b1fa2', light: '#f3e5f5', chip: '#6a1b9a' },
    'Contact Validation': { primary: '#00796b', light: '#e0f2f1', chip: '#00695c' },
    'Philanthropy': { primary: '#5d4037', light: '#efebe9', chip: '#4e342e' },
    'Affiliations': { primary: '#303f9f', light: '#e8eaf6', chip: '#283593' },
    'Social Media': { primary: '#e91e63', light: '#fce4ec', chip: '#c2185b' },
    'News': { primary: '#455a64', light: '#eceff1', chip: '#37474f' },
    'Scores': { primary: '#1e88e5', light: '#e3f2fd', chip: '#1565c0' },
    'Suggested Ask': { primary: '#43a047', light: '#e8f5e9', chip: '#2e7d32' }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs 
          value={value} 
          onChange={handleChange} 
          aria-label="results categories"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              backgroundColor: tabColors[tabLabels[value] as keyof typeof tabColors]?.primary || '#1976d2',
            },
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
        >
          {tabLabels.map((label, index) => {
            // Calculate count including external data sources
            let count = categorizedData[label]?.length || 0;
            
            // Add counts from external data sources
            if (label === 'Social Media') {
              // Count AI insights if available
              if (aiInsightsResults['Social Media']) {
                count++;
              }
            } else if (label === 'Philanthropy') {
              if (philanthropyData?.records && philanthropyData.records.length > 0) {
                count += philanthropyData.records.length;
              }
            } else if (label === 'Contact Validation') {
              if (phoneValidationData?.mobile_phones) {
                count += phoneValidationData.mobile_phones.length;
              }
              if (phoneValidationData?.landline_phones) {
                count += phoneValidationData.landline_phones.length;
              }
              if (emailValidationData?.emails_found) {
                count += emailValidationData.emails_found.length;
              }
            } else if (label === 'Scores') {
              // Count score fields
              const scoresData = data.scores || {};
              if (scoresData.capacity?.capacity_score || scoresData.capacity) count++;
              if (scoresData.propensity?.propensity_score || scoresData.propensity) count++;
              if (scoresData.planned_giving?.planned_giving_score || scoresData.planned_giving) count++;
              if (scoresData.overall?.overall_score || scoresData.overall) count++;
            } else if (label === 'Suggested Ask') {
              // Count suggested ask fields
              if (data.suggested_ask) {
                if (data.suggested_ask.low_ask) count++;
                if (data.suggested_ask.mid_ask) count++;
                if (data.suggested_ask.high_ask) count++;
              }
            } else if (label === 'News') {
              // Count Google News articles
              if (googleNewsData && googleNewsData.articles) {
                count += googleNewsData.articles.length;
              }
            } else if (label === 'Affiliations') {
              // Count AI insights if available
              if (aiInsightsResults['Affiliations']) {
                count++;
              }
            }
            
            // Count AI insights for all supported AI categories
            if (['Profile', 'Financial', 'Political Interests', 'News', 'Consumer Behavior'].includes(label)) {
              if (aiInsightsResults[label]) {
                count++;
              }
            }
            
            const colors = tabColors[label as keyof typeof tabColors];
            const isSelected = value === index;
            
            return (
              <Tab
                key={label}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'inherit',
                        color: isSelected ? colors.primary : 'inherit'
                      }}
                    >
                      {label}
                    </Typography>
                    <Chip 
                      label={count} 
                      size="small" 
                      sx={{ 
                        height: 20, 
                        fontSize: '0.75rem',
                        backgroundColor: count > 0 ? (isSelected ? colors.primary : colors.light) : '#f5f5f5',
                        color: count > 0 ? (isSelected ? 'white' : colors.chip) : '#9e9e9e',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  </Box>
                }
                id={`results-tab-${index}`}
                aria-controls={`results-tabpanel-${index}`}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: colors.light,
                    '& .MuiTabs-indicator': {
                      backgroundColor: colors.primary,
                    }
                  }
                }}
              />
            );
          })}
        </Tabs>
      </Paper>
      
      {tabLabels.map((label, index) => (
        <TabPanel key={label} value={value} index={index}>
          {createExpandableResults(label, categorizedData[label] || [])}
        </TabPanel>
      ))}
    </Box>
  );
};

export default TabbedResults;